import React, { useState, useEffect, useLayoutEffect, useRef, createContext, useContext, useCallback, Suspense } from "react";
import { ChevronRight, Search, PackageOpen, Calendar } from 'lucide-react';  // 2026-04-17: full icon migration — replaces custom SVG I.xxx (logo kept as brand)  // X removed when Modal extracted to src/components/Modal.jsx (C1) — its only consumer; LogOut removed when BlitzHeader+MobileNav extracted (C1) — their only consumer; ChevronLeft/Plus/Pencil/Trash2 removed when AdminPanel extracted to src/components/AdminPanel.jsx (C1, 2026-05-29) — its only consumers
import { ADMIN_EMAILS, isAdmin, nowGMT2, todayGMT2, todayDisplayGMT2, genId, stampNew, stampUpdatedAt, countryFlag, AFFILIATES, BRANDS_NETWORKS, getAffiliateName, getBrandName, AGENT_ALIASES, normalizeAgent, PEOPLE_COLORS, getPersonColor, getAvailableStatuses, calcFee, fmtFee, ALL_PAGES, getPageAccess, hashPassword } from './utils.js';
import { canSaveTable as canSaveTableHelper } from './utils/permission-gate.js';
import { computeDcAffiliates } from './lib/computeDcAffiliates.js';
import { STATUS_OPTIONS, OPEN_STATUSES, TYPE_OPTIONS, STATUS_COLORS, CP_STATUS_OPTIONS, CP_STATUS_COLORS, CP_TYPE_OPTIONS, PARTNER_DEAL_STATUS, PARTNER_DEAL_STATUS_COLORS, PARTNER_STATUS_COLORS, PARTNER_ACT_COLORS, GB_TYPE_COLORS, MONTHS, TEAM_NAMES_DEFAULT, INITIAL, CP_INITIAL, CRG_INITIAL, DC_INITIAL, DEALS_INITIAL } from './constants.js';
// CP_STATUS_CHIP + CP_TYPE_CHIP — pure chip palettes moved verbatim to constants.js
// (C1 refactor). Not used inside App.jsx itself — re-exported from the barrel so
// existing consumers importing them from './App.jsx' (CustomerPayments page) keep
// working unchanged. Behavior-preserving.
export { CP_STATUS_CHIP, CP_TYPE_CHIP } from './constants.js';
import * as Sentry from '@sentry/react';
import { sentryBeforeSend } from './utils/sentry-phone-mask.js';
import { darkModeCSS } from './styles/dark-mode-css.js';
import { injectionsThemeCSS } from './styles/injections-theme-css.js';
import { userBackgroundCSS } from './styles/user-background-css.js';
import { globalTableCSS } from './styles/global-table-css.js';
import { mobileCSS } from './styles/mobile-css.js';
import { designTokens } from './styles/design-tokens.js';
// `inp` is no longer imported for local use here (its last App.jsx consumer, CopyInput,
// moved to src/components/CopyInput.jsx and imports `inp` directly). External pages still
// import `inp` from './App.jsx' (barrel surface) — re-exported below (see the styles
// barrel block) so they keep working unchanged. Behavior-preserving.

// Leaf presentational components extracted to src/components/ (C1 refactor).
// These are not used inside App.jsx itself — they are re-exported so existing
// consumers that import them from './App.jsx' (barrel surface) keep working
// unchanged after the extraction. Behavior-preserving.
export { GroupHeader } from './components/GroupHeader.jsx';
export { InlineCell } from './components/InlineCell.jsx';
export { ChipSelect, MultiChipFilter } from './components/ChipFilters.jsx';
// NameCombo + ThemeToggleButton extracted to src/components/ (C1 refactor). Not
// rendered inside App.jsx itself — re-exported so existing consumers importing them
// from './App.jsx' (barrel surface) keep working unchanged. Behavior-preserving.
export { NameCombo } from './components/NameCombo.jsx';
export { ThemeToggleButton } from './components/ThemeToggleButton.jsx';
// BlitzHeader + MobileNav + NavDropdown extracted to src/components/ (C1 refactor,
// behavior-preserving). BlitzHeader is NOT rendered inside App.jsx itself — every page
// module renders its own <BlitzHeader> and imports it from this barrel surface
// (15 pages: Overview, Dashboard, CustomerPayments, CRGDeals, DailyCap, Deals, Partners,
// MonthlyStats, FtdsInfo, DailyCalcs, GenBalances, Settings, AutoB2026, Injections,
// Analytics, Invoice). Re-exported so they keep working unchanged. MobileNav + NavDropdown
// are composed by BlitzHeader internally (0 external consumers) → no re-export needed.
// v32.13: imported locally (not just re-exported) so the inline AiAdvisorPage
// defined in this file can render <BlitzHeader>. Barrel re-export preserved below.
import { BlitzHeader } from './components/BlitzHeader.jsx';
export { BlitzHeader };
// renderTelegramNotInitiated (login/error banner helper) extracted to
// src/components/TelegramNotInitiated.jsx (C1 refactor). App.jsx still CALLS it (login
// form error render), so it is imported below for local use.
import { renderTelegramNotInitiated } from './components/TelegramNotInitiated.jsx';
// Modal + RevertButton + TwoFAScreen extracted to src/components/ (C1 refactor).
// TwoFAScreen (LoginScreen) is still RENDERED inside App.jsx — imported for local use.
// Modal is no longer imported for local use here (its last App.jsx consumer, AdminPanel,
// moved to src/components/AdminPanel.jsx and imports Modal directly), but Modal +
// RevertButton have external consumers importing them from './App.jsx' (barrel surface)
// — re-exported below so they keep working unchanged. Behavior-preserving. (RefreshButton
// no longer imported here: its only App.jsx consumer was BlitzHeader, now in
// src/components/BlitzHeader.jsx, which imports RefreshButton directly.)
import TwoFAScreen from './components/TwoFAScreen.jsx';
// LoginScreen extracted to src/components/LoginScreen.jsx (C1 refactor, behavior-preserving).
// App.jsx's auth gate renders <LoginScreen> when there is no active user — imported for
// local use only (0 external consumers → no re-export). Default export.
import LoginScreen from './components/LoginScreen.jsx';
// 2026-08-18 — модалка «Session expired». Рендерится в корне App (рядом с AppInner):
// у AppInner шесть ранних return'ов, и вставлять её пришлось бы в каждый.
import { SessionExpiredGate } from './components/SessionExpiredDialog.jsx';
// 2026-09-27 (design v2, P5) — окно «часть правок не сохранилась»: рядом с SessionExpiredGate
// (тот же корень App(), та же логика «это не тост, который можно смахнуть»).
import DroppedSavesNotice from './components/DroppedSavesNotice.jsx';
// 2026-08-31 (Phase 2 «Офлайн видим», П3) — та же причина, что у SessionExpiredGate
// выше: admin/security/botmessages выходят из AppInner ранними return (см. ниже
// ADMIN_ONLY_PAGES), и баннер, вставленный внутри header-ветки, остался бы невидим
// именно там, где сотрудник может застрять надолго. Сиблинг ПЕРЕД AppInner в DOM.
import { OfflineBanner } from './components/OfflineBanner.jsx';
import {
  suppressSessionExpiredNotice,
  resetSessionExpiredNotice,
  localSessionExpired,
  isSessionKnownDead,
} from './lib/session-expiry.js';
// 2026-08-31 (Phase 1 session TTL, П2/П4): triggers useEffect + cross-tab storage listener.
// (fix-round FR4: storage no longer dispatches SESSION_EXPIRED_CLEARED_EVENT — see the
// storage useEffect below — so that import was dropped.)
import {
  checkLocalSessionExpiry,
  clearLocalSuspicion,
} from './lib/session-expiry.js';
import { resumeRowQueues } from './lib/api-client.js';
// 2026-09-27 (design v2, предупреждение о несохранённых правках) — репортёр склада
// blitz_dropped_saves серверу: installDroppedSaveReporter вызывается один раз из корня
// App() (см. ниже), reportFreshDroppedSaves — ещё и явно из completeLogin (вход снимает
// паузу очередей тем же приёмом, что resumeRowQueues/flushPendingSaves чуть ниже).
import { installDroppedSaveReporter, reportFreshDroppedSaves } from './lib/dropped-save-reporter.js';
// 2026-09-01 (фаза3 П2, свод P5) — модульный колбэк-регистр для входа-в-модалке
// (SessionExpiredDialog.jsx читает getReloginContext() напрямую — без пропа: Gate
// рендерится в App(), completeLogin живёт в AppInner, поднять одно к другому нельзя).
import { setReloginContext, clearReloginContext } from './lib/relogin-context.js';
import { lockSkipSave, holdBootSkipSave, releaseBootSkipSave, isBootSkipSaveHeld, markBootFailed, shouldRemindBootFailed } from './lib/skip-save-lock.js';
// CrashGuard (top-level ErrorBoundary) extracted VERBATIM to src/components/CrashGuard.jsx
// (C1 refactor, 2026-05-29, behavior-preserving — class body byte-identical). Imported for
// local use (App root renders <CrashGuard><AppInner/></CrashGuard>); the named re-export
// below keeps the './App.jsx' barrel surface intact for any external consumer.
import CrashGuard from './components/CrashGuard.jsx';
export { CrashGuard } from './components/CrashGuard.jsx';
// Admin sub-pieces (UnbanIPWidget, PageAccessToggles, telegramTest/telegramNotify) were
// extracted from App.jsx in C1 Phase 2d3a. Their only App.jsx consumer was AdminPanel,
// now in src/components/AdminPanel.jsx — so AdminPanel imports them directly and App.jsx
// no longer imports them. All module-private (0 external consumers → no re-export).
export { Modal } from './components/Modal.jsx';
export { RevertButton } from './components/RevertButton.jsx';
// CurrencyBadges + CopyInput + BulkActionBar — small presentational leaves extracted to
// src/components/ (C1 refactor, behavior-preserving). None is rendered inside App.jsx
// itself — re-exported so existing consumers importing them from './App.jsx' keep
// working unchanged: CurrencyBadges (DailyCap/FtdsInfo/CRGDeals/MonthlyStats/Dashboard/
// Deals/Overview/CustomerPayments), CopyInput (CustomerPayments/Dashboard), BulkActionBar
// (CustomerPayments/FtdsInfo/Dashboard/Deals).
export { CurrencyBadges } from './components/CurrencyBadges.jsx';
export { CopyInput } from './components/CopyInput.jsx';
export { BulkActionBar } from './components/BulkActionBar.jsx';
// TEAM_NAMES_REF — shared mutable singleton extracted to src/lib/team-names.js (C1
// refactor). App.jsx WRITES it (populates `.current` from server data); NameCombo
// READS it. Both import the same module so the mutation stays visible. Imported here
// for the writer path.
import { TEAM_NAMES_REF } from './lib/team-names.js';
// USER_COLORS_REF — same shared-mutable-singleton pattern as TEAM_NAMES_REF above,
// but for per-user custom colors (user_colors фича, 2026-07-13). Written by the SAME
// "Keep TEAM_NAMES in sync" effect below (§2.2 брифа) — App.jsx watches users[] once,
// both refs refresh together on every WS-push of the users table. Read by getPersonColor
// (src/utils.js) — first link in the resolution chain, wins over legacy PEOPLE_COLORS.
import { USER_COLORS_REF } from './lib/user-colors.js';
// Shared primitives extracted from App.jsx (C1 refactor, behavior-preserving):
//   • VERSION → src/version.js — frontend's VERSION source of truth. Compared by the
//     WebSocket heartbeat against the server VERSION; value must stay byte-identical
//     (mismatch = 15-sec reload loop). Re-exported for Settings page (1 external).
//   • I → src/icons.jsx — custom SVG icon set. Module-private here (0 external consumers)
//     → imported only for local use, no re-export.
//   • Field → src/components/Field.jsx — labeled form-field wrapper. Re-exported for the
//     7 page modules importing it from the barrel (CustomerPayments, DailyCap, FtdsInfo,
//     CRGDeals, Deals, Dashboard, Partners).
import { VERSION } from './version.js';
import { I } from './icons.jsx';
export { VERSION } from './version.js';
// Field is no longer imported for local use here (its last App.jsx consumer, CopyInput,
// moved to src/components/CopyInput.jsx and imports Field directly), but Field has
// external consumers importing it from './App.jsx' (barrel surface) — re-exported below
// so they keep working unchanged. Behavior-preserving.
export { Field } from './components/Field.jsx';
// SecurityPanel extracted to src/components/ (C1 refactor, behavior-preserving). Used
// internally by the "security" page router. 0 external consumers → imported for local use
// only, no re-export. ActivityLogsViewer + ServerDiagnostics + RestoreDatabase + SyncStatus
// were AdminPanel-only children; they are no longer imported here (AdminPanel imports them
// directly from its own module — see below).
import { SecurityPanel } from './components/SecurityPanel.jsx';
import { BotMessagesPanel } from './components/BotMessagesPanel.jsx';
// AdminPanel (User Management page, admin-only route) extracted VERBATIM to
// ./components/AdminPanel.jsx (C1 monolith split, 2026-05-29). It composes SyncStatus +
// ActivityLogsViewer + ServerDiagnostics + RestoreDatabase + UnbanIPWidget +
// PageAccessToggles + Modal + Field. 0 external importers → not re-exported.
import AdminPanel from './components/AdminPanel.jsx';

// ── Sentry initialization (frontend) ─────────────────────────
// 2026-05-20 (ADR-012): Sentry React SDK для capture JS errors (component
// crashes, async rejections, eval errors, etc.) + auto-grouping в dashboard.
//
// Activation: requires `VITE_SENTRY_DSN` build-time env var (Vite inlines it
// at build time). If unset, Sentry loads gracefully disabled — `Sentry.init`
// is а no-op and ErrorBoundary captures fall к local-only handling.
//
// Note: VITE_SENTRY_DSN is а **PUBLIC** key — it ships в the client bundle.
// Sentry uses а separate project secret для auth; the public DSN cannot be
// abused beyond submitting events к the configured project. See Sentry docs
// для DSN vs auth-token distinction.
//
// Defense-in-depth: existing CrashGuard ErrorBoundary (~line 5980) continues
// к POST к /api/crash-report (backend audit + Telegram alert). Sentry capture
// is additive — fires alongside the existing backend path. If frontend can't
// reach our /api/crash-report (offline, CSP block, etc.) Sentry still gets
// the error через its own transport.
if (typeof window !== 'undefined' && import.meta.env.VITE_SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE || 'production',
      tracesSampleRate: 0.1,
      release: import.meta.env.VITE_APP_VERSION,
      beforeSend: sentryBeforeSend,
    });
    console.log('✅ Sentry initialized (frontend)');
  } catch (e) {
    console.warn('⚠️  Sentry init failed (frontend):', e?.message || e);
  }
} else if (typeof window !== 'undefined') {
  console.log('⚠️  Sentry disabled (VITE_SENTRY_DSN not set)');
}

// Phase 2+3 strangler-fig extraction — pages moved to src/pages/<Name>/index.jsx.
// React.lazy + per-callsite Suspense wrap means each page gets its own code-split chunk,
// loaded on-demand при первом navigate. Pattern reused для Wave 1 extracts.
//
// Overview EAGER (default landing page after login — eliminates Suspense fallback на
// most-visible page). +50 KB to main bundle, but no "Loading…" flicker.
import OverviewDashboard from './pages/Overview';

// Lazy для остальных 14 pages — split chunks, preloaded immediately at module load below.
const SettingsPage = React.lazy(() => import('./pages/Settings'));
const AutoB2026Page = React.lazy(() => import('./pages/AutoB2026'));
// v32.16.65 (2026-06-10): AnalyticsPage lazy import removed — вкладка Analytics убрана из CRM
// по запросу owner. Source остаётся в src/pages/Analytics/ (dormant, в bundle не попадает).
// Backend /api/analytics/* + leeds-sync НЕ тронуты. Restore: вернуть lazy import + prefetch
// ниже + route в page-chain (~2469) + nav entries (BlitzHeader NAV_GROUPS/allNavPages,
// MobileNav MOBILE_GROUPS) + 'analytics' в shared/pages.json и shared/schemas/user.js.
const InjectionsPage = React.lazy(() => import('./pages/Injections'));
const GenBalancesPage = React.lazy(() => import('./pages/GenBalances'));
const InvoiceGeneratorPage = React.lazy(() => import('./pages/Invoice'));
const PartnersHub = React.lazy(() => import('./pages/Partners/conferences/PartnersHub.jsx'));
const RosterPage = React.lazy(() => import('./pages/Roster'));
const BrokersPage = React.lazy(() => import('./pages/Brokers'));
const PhoneCheckPage = React.lazy(() => import('./pages/PhoneCheck'));
const RadarPage = React.lazy(() => import('./pages/Radar'));
const DealsPage = React.lazy(() => import('./pages/Deals'));
const FtdsInfoPage = React.lazy(() => import('./pages/FtdsInfo'));
const MonthlyStatsPage = React.lazy(() => import('./pages/MonthlyStats'));
const DailyCalcsPage = React.lazy(() => import('./pages/DailyCalcs'));
const DailyCap = React.lazy(() => import('./pages/DailyCap'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const CustomerPayments = React.lazy(() => import('./pages/CustomerPayments'));
const CRGDeals = React.lazy(() => import('./pages/CRGDeals'));

// Module-level preload — fires AS SOON AS App.jsx parsed, parallel с initial render.
// Beats user navigation (preload starts before AppInner mounts). Overview не нужно
// preload — она eager выше (часть main bundle).
// Skipped under vitest (MODE==='test'): the fire-and-forget dynamic imports resolve
// AFTER the test env tears down → "Cannot load … after environment was torn down"
// unhandled rejections (exit 1 despite 0 failures). Tests load pages on-demand via
// Suspense, which they await — they never need the background prefetch. vite replaces
// import.meta.env.MODE at build, so prod/dev still preload.
if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
  Promise.resolve().then(() => {
    console.log('🚀 [boot] Preloading 15 lazy page chunks для instant navigation…');
    import('./pages/Settings');
    import('./pages/AutoB2026');
    import('./pages/Injections');
    import('./pages/GenBalances');
    import('./pages/Invoice');
    import('./pages/Partners');
    import('./pages/Roster');
    import('./pages/Deals');
    import('./pages/FtdsInfo');
    import('./pages/MonthlyStats');
    import('./pages/DailyCalcs');
    import('./pages/DailyCap');
    import('./pages/Dashboard');
    import('./pages/CustomerPayments');
    import('./pages/CRGDeals');
  });
}


// renderTelegramNotInitiated — moved to src/components/TelegramNotInitiated.jsx (C1
// refactor, behavior-preserving). Imported at the top of this file for the caller in
// the login form error render.

// ── Shared contexts + hooks extracted to src/contexts/ + src/hooks/ (C1 refactor).
// Behavior-preserving: the context OBJECTS, consumer hooks, and the self-contained
// ToastProvider now live in dedicated modules. App.jsx still RENDERS the providers
// (<ThemeContext.Provider> + <ToastProvider> below) holding the actual state, so it
// imports these for local use AND re-exports them — many other files import these
// symbols from './App.jsx' (barrel surface), and the re-exports keep them working
// unchanged. The new modules import ONLY from 'react' (no cycle back to App.jsx).
import { ThemeContext, useTheme } from './contexts/theme-context.js';
import { ToastContext, useToast, ToastProvider } from './contexts/toast-context.jsx';
import { TOAST_EVENT, dispatchToast } from './lib/toast-event.js';
// v32.16.24: useMobile imported locally again — AiAdvisorPage uses it for its
// responsive single-column mobile layout (the page was desktop-only two-column).
// BlitzHeader still imports it directly. Re-exported below so the barrel surface
// (src/pages/* importing useMobile from the App barrel) stays unchanged.
import { useMobile } from './hooks/use-mobile.js';
export { ThemeContext, useTheme } from './contexts/theme-context.js';
export { ToastContext, useToast, ToastProvider } from './contexts/toast-context.jsx';
export { useMobile };

// MobileNav -- moved to src/components/MobileNav.jsx (C1 refactor,
// behavior-preserving). Composed by BlitzHeader on mobile; 0 external consumers,
// not rendered inside App.jsx -> no import / no re-export here.

/* ── Password Hashing (SHA-256 pure JS - works on HTTP) ── */
// hashPassword — moved to utils.js

// INITIAL_USERS — moved to src/lib/initial-users.js (C1 refactor). App.jsx no longer uses
// it (seed-merge removed 2026-10-21); consumers: LoginScreen, AdminPanel.

// ADMIN_EMAILS, isAdmin — moved to utils.js
// VERSION — moved to src/version.js (C1 refactor). It is the frontend's VERSION source of
// truth (one of the 4 sync places: server.cjs / src/version.js / package.json / index.html;
// mismatch = 15-sec WS-heartbeat reload loop). Imported + re-exported from the barrel above.

// INITIAL_BUILD_HASH — moved to src/lib/build-version.js (C1 refactor). It is the
// build-hash reload-check value compared against the server's BUILD_HASH in the WS
// heartbeat (msg.bh !== INITIAL_BUILD_HASH => reload). Imported below for local use
// (connectWebSocket's version check) + re-exported from the barrel for backward compat.

// nowGMT2, todayGMT2, todayDisplayGMT2 — moved to utils.js


// ═══════════════════════════════════════════════════════════════
// v10.09: DEFAULT AFFILIATE & BRAND/NETWORK LOOKUP TABLES
// Used for dropdowns, name resolution, and Telegram bot mapping
// ═══════════════════════════════════════════════════════════════
// AFFILIATES — moved to utils.js

// BRANDS_NETWORKS — moved to utils.js

// getAffiliateName, getBrandName — moved to utils.js

// ── Storage Layer ──
// Priority: API (shared between all users) > localStorage (offline backup)
// 2026-05-29 EXTRACTED LS_KEYS + lsGet + lsSave к src/lib/local-storage.js
// (C1 Phase 2e shared-helpers). lsSave re-exported below for backward compat.
// 2026-05-29 LS_VERSIONS_KEY EXTRACTED to ./lib/local-storage.js (C1 Phase 2d3b2)
// — shared with RestoreDatabase (also extracted). App.jsx still uses it in the
// version-upgrade cleanup block + load/savePersistedVersions below.
import { LS_KEYS, lsGet, lsSave, LS_VERSIONS_KEY } from './lib/local-storage.js';
export { lsSave };

// 2026-05-29 EXTRACTED INITIAL_BUILD_HASH + PREV_VERSION_KEY + prevVersion к
// src/lib/build-version.js (C1 refactor — build-hash reload-check cluster). Imported
// for local use: INITIAL_BUILD_HASH by connectWebSocket's WS-heartbeat version check;
// PREV_VERSION_KEY + prevVersion by the version-upgrade side-effect block below.
// INITIAL_BUILD_HASH was previously `export const` (barrel surface) — re-exported
// below for backward compat. This is a DIFFERENT concern from ./data-versions.js
// (per-table data versions). build-version.js imports nothing from App.jsx (no cycle).
import { INITIAL_BUILD_HASH, PREV_VERSION_KEY, prevVersion } from './lib/build-version.js';
export { INITIAL_BUILD_HASH };

// ── Version change detection: clear stale localStorage on upgrade ──
// PREV_VERSION_KEY + prevVersion — moved to src/lib/build-version.js (C1 refactor),
// imported above. The side-effect upgrade block itself stays here so its module-init
// ordering (relative to ./data-versions.js's LS_VERSIONS_KEY load) is unchanged.
if (prevVersion !== VERSION) {
  console.log(`🔄 Version upgrade: ${prevVersion || 'unknown'} → ${VERSION}`);
  // v10.25: Only clear version tracking — do NOT purge table data
  // Server is the source of truth. On next fetch, server data will be loaded.
  // Purging localStorage was causing data loss when server fetch failed or raced with autosave.
  try { localStorage.removeItem(LS_VERSIONS_KEY); } catch {}
  localStorage.setItem(PREV_VERSION_KEY, VERSION);
  console.log(`✅ Version updated to ${VERSION} — server data will be fetched on next load`);
}

// v20.08: One-time cleanup of cached deals/offers — removes rows missing affiliate/country/price
// Prevents browser from re-pushing old dirty data to the server
(function cleanLocalStorageDeals() {
  try {
    const key = LS_KEYS['deals'];
    const raw = localStorage.getItem(key);
    if (!raw) return;
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return;
    const before = data.length;
    const clean = data.filter(r => {
      const aff = String(r.affiliate || r.affiliateId || '').trim();
      const country = String(r.country || '').trim();
      const price = String(r.price || r.cpa || '').trim();
      return aff && country && price && country.length <= 15;
    });
    if (clean.length < before) {
      localStorage.setItem(key, JSON.stringify(clean));
      console.log("🧹 Cleaned localStorage deals: " + before + " → " + clean.length + " (removed " + (before-clean.length) + " invalid rows)");
    }
  } catch(e) {}
})();

// 2026-05-29 EXTRACTED dataVersions + loadPersistedVersions + savePersistedVersions
// к src/lib/data-versions.js (C1 refactor — per-table data-version cluster used by
// the save/sync optimistic-concurrency flow). `dataVersions` is a shared const
// object mutated in place (dataVersions[endpoint] = N) — single instance, no setter,
// no reassignment. Imported below; used by apiGet/apiSave/flushPendingSaves +
// connectWebSocket. The build-hash reload-check (INITIAL_BUILD_HASH / PREV_VERSION_KEY
// / prevVersion) is a DIFFERENT concern — now in src/lib/build-version.js.
import { dataVersions, savePersistedVersions } from './lib/data-versions.js';

// 2026-05-29 EXTRACTED API_BASE + WS_URL к src/lib/endpoints.js (C1 Phase 2e
// shared-helpers). API_BASE re-exported below for backward compat (14 external
// importers). WS_URL is App.jsx-internal only (connectWebSocket).
import { API_BASE, WS_URL } from './lib/endpoints.js';
export { API_BASE };

// ── Connection / WebSocket module-level state ──
// 2026-05-29 EXTRACTED к src/lib/connection-state.js (C1 refactor — shared by
// apiGet/apiSave/connectWebSocket + leaf components). READS use the imported
// live-bindings (ESM reflects updates); WRITES go through the setter functions
// (you cannot assign to an imported binding). `pendingSaves` is a shared const
// Map mutated in place (.set/.delete) — no setter, no reassignment.
import {
  serverOnline,
  setServerOnline,
  pendingSaves,
  wsConnection,
  setWsConnection,
  wsReconnectTimer,
  setWsReconnectTimer,
  wsReconnectAttempts,
  setWsReconnectAttempts,
  probeConnection,
  startHealthWatch,
} from './lib/connection-state.js';

// ── Network / session module-level state ──
// 2026-05-29 EXTRACTED к src/lib/network-state.js (C1 refactor — shared by
// apiGet/apiSave/flushPendingSaves/connectWebSocket/handleConflictResolution +
// leaf components/handlers). READS use the imported live-bindings (ESM reflects
// updates); WRITES to the reassigned bindings go through the setter functions (you
// cannot assign to an imported binding). `conflictSetters` / `lastKnownCounts` are
// shared const objects mutated in place (obj[k] = …) and `wsListeners` is a shared
// const Set mutated in place (.add/.delete) — no setter, no reassignment.
import {
  sessionExpiredFlag,
  setSessionExpiredFlag,
  justLoggedIn,
  setJustLoggedIn,
  lastSaveTimestamps,
  setLastSaveTimestamps,
  mergeByIDGlobal,
  setMergeByIDGlobal,
  conflictSetters,
  lastKnownCounts,
  wsListeners,
} from './lib/network-state.js';

// ── Session token management (FIX C4: auth on all endpoints) ──
// 2026-05-26 EXTRACTED к src/utils/session.js (atomic apiAction Authorization
// header fix — same getSessionToken used by api-actions.js без App.jsx coupling).
// App.jsx re-exports `getSessionToken` + `resetSessionTokenCache` для backward
// compat: src/App.test.jsx and src/pages/Settings/index.jsx still import them
// from './App.jsx'. `setSessionToken` was never exported — kept as local binding.
// `authHeaders` still lives here (calls the now-imported getSessionToken).
//
// Phase 2 strangler-fig extraction — anti-pattern 5.21 workaround.
// SettingsPage (extracted into src/pages/Settings/) needs cache-bust semantic
// (force re-read from localStorage on next request, v28.10 fix). ES module
// live-bindings prevent direct assignment к module-scoped `sessionToken` from
// importers, so we expose `resetSessionTokenCache`. All in-file callsites
// now also use `resetSessionTokenCache()` / `setSessionToken(...)` (no direct
// `sessionToken =` assignments remain — verified post-extraction).
import { getSessionToken, setSessionToken, resetSessionTokenCache, getRawSession } from './utils/session.js';
export { getSessionToken, resetSessionTokenCache };
// 2026-05-29 EXTRACTED authHeaders к src/lib/auth-headers.js (C1 Phase 2e
// shared-helpers). Re-exported below for backward compat (13 external importers).
// auth-headers.js imports getSessionToken from ./utils/session.js (no cycle).
import { authHeaders } from './lib/auth-headers.js';
export { authHeaders };

// ── Version tracking per table (for conflict resolution)
// 2026-05-29 `dataVersions` (+ load/savePersistedVersions) EXTRACTED к
// src/lib/data-versions.js — imported above as a shared const singleton.
//
// `sessionExpiredFlag` / `justLoggedIn` EXTRACTED к src/lib/network-state.js
// (imported above). Reads use the live-bindings; writes go through
// setSessionExpiredFlag / setJustLoggedIn.

// ── Network-request layer (apiGet / apiSave / connectWebSocket / flushPendingSaves /
//    logPaymentAction / onWsUpdate / handleConflictResolution) ──
// 2026-05-29 EXTRACTED к src/lib/api-client.js (C1 refactor — network-request
// layer) as a behavior-preserving move. These seven functions form a mutually
// recursive call graph (apiGet<->apiSave, connectWebSocket->flushPendingSaves->
// apiSave, apiSave->handleConflictResolution), so they live together in one module
// to avoid import cycles. api-client.js imports its state from the sibling
// singleton modules (connection-state / network-state / data-versions / tombstones /
// endpoints / auth-headers / build-version / version) — NEVER from App.jsx, so there
// is no cycle (App.jsx imports FROM api-client.js; api-client.js does not import back).
//
// Imported below for internal use (AppInner / debouncedSave / effects call apiGet /
// apiSave / connectWebSocket / onWsUpdate / flushPendingSaves / logPaymentAction /
// handleConflictResolution). apiGet / apiSave / onWsUpdate / logPaymentAction are
// ALSO re-exported from the barrel for backward compat — pages (Dashboard,
// CustomerPayments, Deals, …) and App.test.jsx still `import { ... } from './App.jsx'`.
// (refetch-cp.js / refetch-payment.js / the api-get|api-save integration tests now
// import apiGet/apiSave directly from './lib/api-client.js' — they live in src/lib.)
import {
  apiGet,
  apiSave,
  apiSaveRows,
  connectWebSocket,
  flushPendingSaves,
  logPaymentAction,
  onWsUpdate,
  handleConflictResolution,
  isEndpointForbidden,
  persistDroppedSave,
  _testResetSaveFlowState,
  enqueuePendingSave,
} from './lib/api-client.js';
export { apiGet, apiSave, onWsUpdate, logPaymentAction };
import { createAppWsListener } from './lib/app-ws-listener.js';
import { serverNow } from './lib/server-clock.js';

// ── Pending saves queue: retry failed saves when server comes back ──
// `pendingSaves` extracted к src/lib/connection-state.js (imported above) — it is
// a shared const Map mutated in place (.set/.delete/.clear); same instance across
// modules. NOT reassigned, so no setter needed.
// `lastSaveTimestamps` extracted к src/lib/network-state.js (imported above) —
// reads use the live-binding; the {} reset goes through setLastSaveTimestamps;
// per-endpoint writes (lastSaveTimestamps[endpoint] = now) mutate the shared object
// in place. FIX C1: tracks when each table was last saved to prevent echo saves.
// 2026-05-19 — test helper: vitest can reset module-level apiSave state between tests
// к ensure clean baseline. NOT для production use — internal/_-prefixed naming convention.
export function _testResetApiSaveState() {
  setLastSaveTimestamps({});
  if (pendingSaves && typeof pendingSaves.clear === 'function') pendingSaves.clear();
  _testResetSaveFlowState();
}

// ── Conflict resolution: when apiSave gets 409, merge server data into React state ──
// `conflictSetters` extracted к src/lib/network-state.js (imported above) — shared
// const object mutated in place (conflictSetters[table] = setter); single instance,
// no setter, no reassignment. Registered by React component: { 'payments': setPayments, ... }
function registerConflictSetter(table, setter) { conflictSetters[table] = setter; }

// handleConflictResolution — moved to src/lib/api-client.js (C1 refactor). Called by
// apiSave on HTTP 409; reads conflictSetters + mergeByIDGlobal from network-state.js.

// `mergeByIDGlobal` (global reference to mergeByID, set by React component after
// mount) extracted к src/lib/network-state.js (imported above). Read here by
// handleConflictResolution; written once by AppInner via setMergeByIDGlobal.

// flushPendingSaves (+ its `setInterval(flushPendingSaves, 30000)` module-init drain
// timer) — moved to src/lib/api-client.js (C1 refactor). Drains the pendingSaves queue
// via apiSave; the setInterval fires at api-client.js module-load (same as before).

// ── v31.89: Payment action telemetry beacon ──
// Used by Dashboard handleStatusChange + CustomerPayments handleCpStatusChange.
// Independent path от apiSave — backend monitor (notification-monitor.cjs) correlates
// click logs vs backend transition logs. Gap = save dropped client→server (potential
// v31.87 race regression). See backend/services/notification-monitor.cjs для full design.
//
// 2026-05-18 PR #140 — sendBeacon migration. Production analysis on 2026-05-18 revealed
// Y Admin's Android Chrome dropped 4 of 6 telemetry POSTs in cascade window (66% drop rate).
// Across 5 days of nginx logs, only 5 telemetry POSTs landed для entire CRM. PR #128 moved
// logPaymentAction outside React updater — necessary but не sufficient. Real root cause:
// fetch(keepalive:true) is unreliably delivered on mobile browsers (battery saver, background
// tabs, network throttling). navigator.sendBeacon API guaranteed-queue design for telemetry.
//
// sendBeacon limitations:
//   • Cannot set custom headers (no Authorization possible)
//   • Workaround: embed session token в body field `_t` — backend telemetry route extracts
//   • Cookies sent automatically — but our app uses Bearer tokens, не cookies
// Falls back to fetch+keepalive if sendBeacon unavailable (very old browsers, IE-era).
// Fire-and-forget semantics preserved — silent fail (telemetry never blocks UI).
// logPaymentAction — moved to src/lib/api-client.js (C1 refactor). v31.89 telemetry
// beacon (sendBeacon, fire-and-forget). Re-exported from the barrel above + still used
// in-file by logStatusChangeIfDifferent below.

// 2026-05-18 PR #130 — centralized telemetry helper для status transition tracking.
//
// Why this exists. PR #128 + #129 instrumented только ChipSelect/dropdown handlers
// (handleStatusChange / handleCpStatusChange). Production analysis post-PR #128 deploy
// revealed 3 additional Anomaly D alerts from user Rose (office1092021):
//   • payment 7a2239201 (invoice "13") Approved → Paid       — likely Edit modal Save
//   • Legion              Open → Received (customer-payments) — likely 🔍 Check TX button
//   • TopNet              Open → Received (customer-payments) — likely 🔍 Check TX button
//
// Audit found 4 firing-status paths без telemetry instrumentation:
//   1. handleSave (Edit modal — both pages)
//   2. handleCheckTx (CP single-payment 🔍 Check TX button)
//   3. handleCheckAll (CP bulk 🔍 Check All Open Invoices)
//   4. RevertButton onRevert (rare, separate scope)
//
// This helper consolidates все callsites into single function с:
//   • Status-changed check (skip if oldStatus === newStatus — no transition)
//   • Race-delete guard (skip if oldPayment undefined OR no invoice — same guard as PR #129)
//   • Standard logPaymentAction call с consistent payload shape
//
// Callers pass `oldPayment` object captured BEFORE setPayments updater runs (или from
// closure scope — handleCheckTx receives invoice object as parameter). Helper handles
// the comparison internally, eliminating per-callsite if/else churn.
export function logStatusChangeIfDifferent(table, paymentId, oldPayment, newStatus) {
  if (!oldPayment) return; // race-deleted between user action and helper invocation
  const oldStatus = oldPayment.status || '';
  const invoice = oldPayment.invoice || '';
  if (!invoice) return; // no invoice means либо race-delete либо malformed record
  if (oldStatus === newStatus) return; // no transition — Anomaly D wouldn't fire either
  logPaymentAction(table, paymentId, invoice, oldStatus, newStatus);
}

// 2026-05-29 EXTRACTED к src/lib/tombstones.js (C1 refactor — soft-delete /
// tombstone tracking cluster: the deletedIDs/persistDeletedIDs/tombstonedIDs
// const-singleton objects + trackDelete + isDeleted + mergeOffersIntoDeals +
// processTombstoned + filterTombstoned + getAndClearDeletes). Shared by the
// network layer (apiGet/apiSave/connectWebSocket/flushPendingSaves below) AND by
// many page components. Re-exported below for backward compat (page importers +
// the lib test files receive these via `from '../App.jsx'`).
import {
  deletedIDs,
  persistDeletedIDs,
  tombstonedIDs,
  trackDelete,
  mergeOffersIntoDeals,
  processTombstoned,
  filterTombstoned,
  getAndClearDeletes,
} from './lib/tombstones.js';
// mergeByID — THE WS/poll/conflict merge engine (C1 extract 2026-05-29). Near-pure:
// reads only its args + module-level singletons (lastKnownCounts, deletedIDs/
// persistDeletedIDs/tombstonedIDs). Registered for the global 409-conflict path via
// setMergeByIDGlobal(mergeByID) below.
import { mergeByID, computeBootMergePush } from './lib/merge-by-id.js';
import { hasPendingAtomicSnapshots } from './lib/finance-page-merge.js';
import { runAtomicAutosaveBaselineGate } from './lib/atomic-autosave-suppression.js';
// 2026-06-04 (refund-family audit, site a): pure CP/payments save-filter predicate — keeps
// negative refunds (was `amt > 0` which dropped them before POST). Unit-tested in lib.
import { keepPaymentRow } from './lib/finance-row-filters.js';

// FR2 (fix-round 2026-08-31, жюри A CRITICAL-2): rescue for local-only payments/
// customer-payments records that mergeByID's SYNC-mode branch drops past MAX_LOCAL_AGE
// (7d). Sync fires on every WS-broadcast AND every 15s poll tick — module-level (not
// per-render) Set so a record that's STILL stale on the next tick doesn't re-enter
// blitz_dropped_saves again: the store caps at 10 entries (pruneDroppedSaves,
// api-client.js), and repeat-rescuing the SAME id would evict genuinely different
// records. Deliberately scoped to payments/customer-payments only — other tables don't
// carry the same finance-durability contract and are out of this fix's scope.
const rescuedStaleSyncIds = new Set();
// Exported for unit testing (src/lib/rescue-stale-sync-record.test.js) — same extraction
// pattern as classifySessionProbe/computeBootMergePush: the idempotency behavior (module-
// level Set, not per-render state) can't be observed by mounting AppInner per test.
export function rescueStaleSyncRecord(table, record) {
  if (table !== 'payments' && table !== 'customer-payments') return;
  if (!record || !record.id || rescuedStaleSyncIds.has(record.id)) return;
  rescuedStaleSyncIds.add(record.id);
  persistDroppedSave(table, { data: [record], reason: 'merge-stale-local' });
}

export {
  deletedIDs,
  persistDeletedIDs,
  tombstonedIDs,
  trackDelete,
  mergeOffersIntoDeals,
  processTombstoned,
  filterTombstoned,
  getAndClearDeletes,
};

// stampUpdatedAt — moved to utils.js

// 2026-05-29: pure decision helper extracted from the proactive session-restore probe
// in AppInner (the v31.66 logout-race fix). Given the /api/session probe's HTTP status
// and parsed body, it decides what the probe should do — side effects (setSessionToken /
// setUser / saveSession) stay in the effect, so the auth decision is unit-testable.
// Behavior is identical to the prior inline checks:
//   - status 401                              → 'logout'  (authoritative server reject)
//   - 2xx body {ok:true, user}                → 'refresh' (apply fresh user)
//   - anything else (5xx/429/403/ok-no-user)  → 'keep'    (transient — keep local session)
// `body` is the parsed JSON when the response was ok, else null — mirroring the inline
// code which only called json() inside `if (probeRes.ok)`. Only a 401 logs the user out;
// transient errors must NOT (that was the logout-race the v31.66 fix addressed).
export function classifySessionProbe(status, body) {
  if (status === 401) return { action: 'logout' };
  if (body && body.ok && body.user) return { action: 'refresh', user: body.user };
  return { action: 'keep' };
}

// `lastKnownCounts` (track last known record counts per table to detect data loss)
// extracted к src/lib/network-state.js (imported above) — shared const object mutated
// in place (lastKnownCounts[endpoint] = N); single instance, no setter, no reassignment.

// 2026-05-19 PR — added `_isRetry` parameter (4th, internal) to bound throttled-response
// retries к exactly 1. Without this, sustained-rapid-write scenarios could ping-pong с
// server throttle indefinitely. External callers should never pass _isRetry — only the
// internal retry path uses it. Default false.
// apiSave — moved to src/lib/api-client.js (C1 refactor). Mutual with apiGet (recovery
// path) + calls handleConflictResolution on 409. Re-exported from the barrel above.

// ── WebSocket Manager (real-time sync) ──
// `wsConnection` / `wsReconnectTimer` / `wsReconnectAttempts` extracted к
// src/lib/connection-state.js (imported above). Reads use live-bindings; writes
// go through setWsConnection / setWsReconnectTimer / setWsReconnectAttempts.
// `wsListeners` extracted к src/lib/network-state.js (imported above) — shared const
// Set mutated in place (.add/.delete); single instance, no setter, no reassignment.

// connectWebSocket — moved to src/lib/api-client.js (C1 refactor). Uses INITIAL_BUILD_HASH
// + VERSION (heartbeat reload check), wsListeners fan-out, flushPendingSaves on reconnect.

// 2026-05-22 PR (this) — exported для pages that need direct WS subscription
// (e.g., DealsPage offer-labels Object-payload sync). Module-level wsListeners
// Set is shared с central listener at line 5361 — multi-subscriber pattern.
// Each subscriber gets EVERY msg (after Array-filtered branch on line 1675);
// subscriber is responsible для filtering by msg.table + msg.type itself.
// onWsUpdate — moved to src/lib/api-client.js (C1 refactor). Registers a WS fan-out
// listener (wsListeners.add). Re-exported from the barrel above (DealsPage subscribes).

// ── useDebouncedSave: DEPRECATED (v11.02) — replaced by debouncedSave() in AppInner ──
// Kept as stub to avoid breaking any external references; do not use.
function useDebouncedSave() {}

// Telegram API functions — telegramTest / telegramNotify moved to
// src/lib/telegram-admin.js (C1 refactor, Phase 2d3a). Imported at the top of
// this file for the remaining internal user (AdminPanel Telegram test buttons).

// STATUS_OPTIONS, OPEN_STATUSES, TYPE_OPTIONS — moved to constants.js
// STATUS_COLORS — moved to constants.js

// MONTHS — moved to constants.js

// genId, stampNew — moved to utils.js

// countryFlag — moved to utils.js

// INITIAL — moved to constants.js

/* ── Icons ── */
// I — moved to src/icons.jsx (C1 refactor). Imported at the top of this file for local
// use (remaining I.xxx references; most icons migrated to lucide-react 2026-04-17).

/* ── Shared styles ── */
// `inp` moved to src/styles/inline-styles.js (C1 Phase 2c) to break the
// App.jsx <-> NameCombo.jsx circular import. Re-exported here to preserve the
// barrel — external pages still import `inp` from './App.jsx'.
export { inp } from './styles/inline-styles.js';
// noScrollNum — number-input scroll-blur handler moved verbatim to utils.js (C1
// refactor). Not used inside App.jsx itself — re-exported from the barrel so existing
// consumers importing it from './App.jsx' (CustomerPayments, Dashboard) keep working
// unchanged. Behavior-preserving.
export { noScrollNum } from './utils.js';

/* ── Components ── */
// TEAM_NAMES_DEFAULT — moved to constants.js
// TEAM_NAMES_REF — moved to src/lib/team-names.js (C1 refactor, shared mutable
// singleton). Imported at the top of this file for the writer below (~populate from
// server data); NameCombo reads the same module-singleton.

// ── Agent name normalization — merge aliases into canonical names ──
// AGENT_ALIASES, normalizeAgent — moved to utils.js

// PEOPLE_COLORS, getPersonColor — moved to utils.js

// NameCombo — moved to src/components/NameCombo.jsx (C1 refactor, behavior-preserving).
// Re-exported from the barrel block near the top of this file for external consumers.

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#F1F5F9", text: "#475569", border: "#94A3B8" };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px 3px 7px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: c.bg, color: c.text, border: `1px solid ${c.border}55`, whiteSpace: "nowrap", letterSpacing: "0.2px" }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.border, flexShrink: 0, boxShadow: `0 0 5px ${c.border}90` }} />
    {status}
  </span>;
}

// CurrencyBadges — moved verbatim to src/components/CurrencyBadges.jsx (C1 refactor).
// Re-exported from the barrel block near the top of this file for external consumers.

// Field — moved to src/components/Field.jsx (C1 refactor). Imported + re-exported from
// the barrel above.

// CopyInput — moved verbatim to src/components/CopyInput.jsx (C1 refactor). Re-exported
// from the barrel block near the top of this file for external consumers
// (CustomerPayments, Dashboard).

// getAvailableStatuses — moved to utils.js

// calcFee, fmtFee — moved to utils.js

// NavDropdown -- moved to src/components/NavDropdown.jsx (C1 refactor,
// behavior-preserving). Composed by BlitzHeader on desktop; 0 external consumers,
// not rendered inside App.jsx -> no import / no re-export here.

/* ── Theme Toggle Button (shared, used in BlitzHeader desktop + MobileNav) ──
   Renders ☀️/🌙 button styled to match its host (header chrome on desktop,
   drawer chrome on mobile). Calls useTheme().toggle on click — state flips,
   localStorage persists (defensive), all `.ai-themed *` descendants cross-fade
   in 200ms via the --ai-* token transition rules. Self-contained: useTheme()
   called directly (H1 Rev 2: no prop drilling from BlitzHeader → MobileNav).
*/
// ThemeToggleButton — moved to src/components/ThemeToggleButton.jsx (C1 refactor,
// behavior-preserving). Re-exported from the barrel block near the top of this file
// for external consumers (Injections page + ThemeToggle test).

/* ── Shared Responsive Header ── */
// BlitzHeader -- moved to src/components/BlitzHeader.jsx (C1 refactor,
// behavior-preserving). Not rendered inside App.jsx (each page renders its own).
// Re-exported from the barrel block near the top of this file for the 15 page
// modules importing it from './App.jsx'.



/* ── Payment Table ── */
// BulkActionBar — moved verbatim to src/components/BulkActionBar.jsx (C1 refactor).
// Re-exported from the barrel block near the top of this file for external consumers
// (CustomerPayments, FtdsInfo, Dashboard, Deals).

// v31.38: chip color mapping для Payment status'ов — используется в новом
// chip-style picker'е (порт pattern'а из Invoice Gen Archive в light theme).
// Solid colors (не gradients) — text + dot рендерятся одним цветом.
export const PAYMENT_STATUS_CHIP = {
  'Open':            { color: '#F59E0B', label: 'Open' },
  'On hold':         { color: '#F97316', label: 'On hold' },
  'On the way':      { color: '#6366F1', label: 'On the way' },
  'Approved to pay': { color: '#0EA5E9', label: 'Approved to pay' },
  'Paid':            { color: '#10B981', label: 'Paid' },
};
export const paymentStatusMeta = (s) => PAYMENT_STATUS_CHIP[s] || { color: '#64748B', label: s || '—' };

// v31.44: chip color mapping для Payment type'ов — used в PaymentForm modal
// через ChipSelect (same pattern as status). Distinct colors не конфликтуют
// с existing status palette:
//   - violet #8B5CF6 для Affiliate Payment (primary outgoing flow)
//   - red #EF4444 для Brand Refund (reverse flow — money coming from brand back)
export const PAYMENT_TYPE_CHIP = {
  'Affiliate Payment': { color: '#8B5CF6', label: 'Affiliate Payment' },
  'Brand Refund':      { color: '#EF4444', label: 'Brand Refund' },
};

// CP_STATUS_CHIP + CP_TYPE_CHIP — moved verbatim to constants.js (C1 refactor) and
// re-exported from the barrel block near the top of this file (CustomerPayments page
// imports them from './App.jsx'). Behavior-preserving.



/* ── Admin Panel ── */
// ALL_PAGES, getPageAccess — moved to utils.js

// PageAccessToggles moved to src/components/PageAccessToggles.jsx (C1 refactor,
// Phase 2d3a). UnbanIPWidget moved to src/components/UnbanIPWidget.jsx. Both
// imported at the top of this file for their internal users (AdminPanel).

// ActivityLogsViewer + SecurityPanel moved to src/components/ (C1 refactor,
// Phase 2d3b). ActivityLogsViewer.jsx (Activity Logs panel, used by AdminPanel) +
// SecurityPanel.jsx (Security Dashboard: audit / sessions / IP mgmt, used by the
// "security" page router). Both imported at the top of this file for their internal
// users. Independent siblings — neither composes the other.

// AdminPanel + its RestoreDatabase/ServerDiagnostics children extracted VERBATIM to
// src/components/AdminPanel.jsx (C1 monolith split, 2026-05-29). Imported at top; the
// admin route below renders <AdminPanel>. Internal-only — no external importers.

/* ── Dashboard ── */
// ═══════════════════════════════════════════════════════════════
// OVERVIEW DASHBOARD — Dedicated analytics & KPI page
// ═══════════════════════════════════════════════════════════════

// ── MonthlyTargetsSection: Full targets editor for Settings page ──
// Extracted to ./components/MonthlyTargetsSection.jsx (C1 monolith split). Re-exported
// here because src/pages/Settings/index.jsx imports it from App.jsx (public surface kept).
export { MonthlyTargetsSection } from './components/MonthlyTargetsSection.jsx';


// ── RevertButton — undo last change on any table page ──────────
/* ── Customer Payments Page ── */
// CP_STATUS_OPTIONS, CP_STATUS_COLORS, CP_TYPE_OPTIONS, CP_INITIAL — moved to constants.js


/* ── CRG Deals Page ── */
// CRG_INITIAL — moved to constants.js






/* ── App ── */

// Session management with token


function generateSessionToken() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

function getSession() {
  try {
    const session = getRawSession();
    if (!session) return null;
    // v31.66: TTL 7d → 23h, чтобы НЕ превышать backend SESSION_TTL (24h, server.cjs:828).
    // PRIOR mismatch (frontend 7d vs backend 24h) был root cause cascade H1+H2:
    // user возвращался через 25-168h, getSession() возвращал valid local session,
    // backend rejected все 11 параллельных apiGet → sessionExpiredFlag set от первого 401
    // → mass setUser(null) → user видел "залогинен и через 2s выкинуло на login".
    // 23h pessimistic margin: даже worst-case clock drift не allow stale local session.
    // Server probe в useEffect[user] (Step 1.5, after setLoaded) — authoritative source если online.
    // 2026-08-31 (Phase 1 session TTL, П2): TTL-математика теперь в session-expiry.js
    // (localSessionExpired) — один дом для порога, checkLocalSessionExpiry (триггеры
    // ниже) переиспользует ровно ту же функцию. Поведение этой ветки не изменилось.
    if (localSessionExpired(session, Date.now())) {
      localStorage.removeItem('blitz_session');
      return null;
    }
    return session;
  } catch { return null; }
}

// 2026-08-31 (Phase 1 session TTL, П1) — loginTime должен означать ВХОД, а не «последний
// раз, когда что-то записали в blitz_session». Раньше saveSession штамповало Date.now()
// БЕЗУСЛОВНО на каждый вызов — а вызывают её же и смена cpDesign/paymentsDesign/
// genBalancesDesign, и серверный rehydrate pageAccess/name, и boot-probe refresh: клик по
// переключателю дизайна «продлевал» локальный 23h-срок, и проверка мерила время не от
// входа. freshLogin:true — единственный случай нового штампа; иначе loginTime существующей
// сессии (raw, БЕЗ TTL-фильтра — сохраняем как есть, даже если он «раздут» прошлым багом:
// ничего не мигрируем, реактивный путь подстрахует) копируется как есть; при полном
// отсутствии существующей сессии (нечего копировать) используем Date.now() как best effort.
function saveSession(user, { freshLogin = false } = {}) {
  const existing = freshLogin ? null : getRawSession();
  const preservedLoginTime = (existing && typeof existing.loginTime === 'number') ? existing.loginTime : null;
  const session = {
    email: user.email,
    name: user.name,
    pageAccess: user.pageAccess,
    cpDesign: user.cpDesign,  // CP layout preference — survives reload (CP redesign 2026-06-21)
    paymentsDesign: user.paymentsDesign,  // Payments tab layout preference — survives reload (Payments premium redesign)
    genBalancesDesign: user.genBalancesDesign,  // Gen Balances layout preference — survives reload (Gen Balances premium redesign)
    token: localStorage.getItem('blitz_token') || getSessionToken() || generateSessionToken(),
    loginTime: preservedLoginTime !== null ? preservedLoginTime : Date.now(),
  };
  localStorage.setItem('blitz_session', JSON.stringify(session));
  localStorage.setItem('blitz_device','1');
  localStorage.setItem('blitz_authorized','1');
  return session;
}

// 2026-08-31 (Phase 1 session TTL) — test-only re-export: getSession/saveSession stay
// module-private (App.jsx implementation detail — same `_`-prefixed test-only convention
// as _testResetApiSaveState elsewhere in this file), but П1's freshLogin classification
// and the TTL boot-check regression need direct unit coverage without mounting the whole
// app. NOT for production use.
export { saveSession as _testSaveSession, getSession as _testGetSession };

function clearSession() {
  // Notify server of logout
  const token = getSessionToken();
  if (token) {
    try { fetch(`${API_BASE}/logout`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }).catch(() => {}); } catch {}
  }
  setSessionToken(null);
  localStorage.removeItem('blitz_session');
  // Note: blitz_token intentionally NOT removed so login form still shows
  connectWebSocket(); // W2: retire current socket, no reconnect without token
}

// ── v32.13: AI Traffic Advisor page ───────────────────────────────────────────
// Inline page component (per spec — not a lazy ./pages module). Talks to the three
// new backend endpoints: POST /api/ai-chat, POST /api/ai-upload, GET /api/ai-files.
// Auth: Bearer token (same source the rest of the app uses). Conversation history
// is kept client-side in localStorage; the knowledge base lives server-side.
const AI_CHATS_LS_KEY = 'blitz_ai_chats';

export function aiAuthHeaders(extra) {
  let token = '';
  try {
    token = localStorage.getItem('blitz_token')
      || (JSON.parse(localStorage.getItem('blitz_session') || '{}').token)
      || '';
  } catch { token = ''; }
  return { ...(extra || {}), 'X-Client-Time': String(Date.now()), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// Minimal, dependency-free markdown → safe HTML (bold, bullets, numbered lists,
// inline code, line breaks). HTML is escaped first so AI output can't inject markup.
function aiRenderMarkdown(text) {
  const esc = String(text || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = esc.split('\n');
  let html = '';
  let listType = null; // 'ul' | 'ol'
  const closeList = () => { if (listType) { html += `</${listType}>`; listType = null; } };
  const inline = (s) => s
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code style="background:rgba(148,163,184,0.18);padding:1px 5px;border-radius:4px;font-family:\'JetBrains Mono\',monospace;font-size:12px">$1</code>');
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const ul = line.match(/^\s*[-*•]\s+(.*)$/);
    if (ol) {
      if (listType !== 'ol') { closeList(); html += '<ol style="margin:6px 0 6px 18px;padding:0">'; listType = 'ol'; }
      html += `<li style="margin:3px 0">${inline(ol[1])}</li>`;
    } else if (ul) {
      if (listType !== 'ul') { closeList(); html += '<ul style="margin:6px 0 6px 18px;padding:0">'; listType = 'ul'; }
      html += `<li style="margin:3px 0">${inline(ul[1])}</li>`;
    } else if (line.trim() === '') {
      closeList(); html += '<div style="height:6px"></div>';
    } else {
      closeList(); html += `<div style="margin:2px 0">${inline(line)}</div>`;
    }
  }
  closeList();
  return html;
}

function AiAdvisorPage({ user, onLogout, onNav, onAdmin, userAccess }) {
  const mobile = useMobile();  // v32.16.24: drives the responsive single-column layout below
  const QUICK_ACTIONS = [
    { icon: "📊", label: "Monthly Review", prompt: "Give me a full performance review for the last full month. Top affiliates by CR%, top brokers by net profit, and which geos are declining." },
    { icon: "🚀", label: "Route Traffic", prompt: "I have 500 leads from Huskey (140) available today. Which brokers and geos should I route them to for maximum conversion?" },
    { icon: "⚠️", label: "Low Performers", prompt: "Which affiliates had less than 1% CR in the last 30 days and should have their caps reduced or stopped?" },
    { icon: "⚖️", label: "Compare Brokers", prompt: "Compare Legion (1192) vs Velo CRG (3672). Which one should get priority traffic this month and why?" },
    { icon: "🔍", label: "Diagnose Geo", prompt: "France CR dropped from 3.2% to 1.3% over the last quarter. What is your diagnosis and what should we do about it?" },
  ];

  const loadChats = () => {
    try { const v = JSON.parse(localStorage.getItem(AI_CHATS_LS_KEY) || '[]'); return Array.isArray(v) ? v : []; }
    catch { return []; }
  };

  const [chats, setChats] = useState(loadChats);
  const [activeId, setActiveId] = useState(() => { const c = loadChats(); return c.length ? c[0].id : null; });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesError, setFilesError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const activeChat = chats.find(c => c.id === activeId) || null;
  const messages = activeChat ? activeChat.messages : [];

  const persist = (next) => {
    setChats(next);
    try { localStorage.setItem(AI_CHATS_LS_KEY, JSON.stringify(next.slice(0, 50))); } catch {}
  };

  const refreshFiles = () => {
    fetch(`${API_BASE}/ai-files`, { headers: aiAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => { setFiles(Array.isArray(data?.files) ? data.files : []); setFilesError(''); })
      .catch(() => setFilesError('Could not load knowledge base'));
  };

  useEffect(() => { refreshFiles(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, loading]);

  const newChat = () => { setActiveId(null); setInput(''); };

  const send = async (textArg) => {
    const text = (typeof textArg === 'string' ? textArg : input).trim();
    if (!text || loading) return;
    setInput('');

    // Resolve / create the active chat synchronously so we send the right history.
    let chat = activeChat;
    let working = chats;
    if (!chat) {
      chat = { id: genId(), title: text.slice(0, 40) || 'New chat', messages: [], updatedAt: nowGMT2().toISOString() };
      working = [chat, ...chats];
      setActiveId(chat.id);
    }
    const priorHistory = chat.messages.map(m => ({ role: m.role, content: m.content }));
    const withUser = working.map(c => c.id === chat.id
      ? { ...c, messages: [...c.messages, { role: 'user', content: text }], updatedAt: nowGMT2().toISOString() }
      : c);
    persist(withUser);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/ai-chat`, {
        method: 'POST',
        headers: aiAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ message: text, history: priorHistory.slice(-10) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const reply = (data && typeof data.reply === 'string' && data.reply) || 'No response.';
      persist(withUser.map(c => c.id === chat.id
        ? { ...c, messages: [...c.messages, { role: 'assistant', content: reply }], updatedAt: nowGMT2().toISOString() }
        : c));
    } catch (e) {
      persist(withUser.map(c => c.id === chat.id
        ? { ...c, messages: [...c.messages, { role: 'assistant', content: `⚠️ The advisor is unavailable right now (${e.message}). Try again in a moment.` }] }
        : c));
    } finally {
      setLoading(false);
    }
  };

  const onUploadPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setUploading(true);
    setFilesError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/ai-upload`, { method: 'POST', headers: aiAuthHeaders(), body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      refreshFiles();
    } catch {
      setFilesError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fmtSize = (n) => {
    const b = Number(n) || 0;
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  const panel = { background: 'rgba(15,20,38,0.6)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 12 };

  return (
    <div style={{ minHeight: mobile ? 'calc(100dvh - 56px)' : 'calc(100vh - 64px)', background: '#0a0f1e', color: '#E2E8F0' }}>
      {/* BlitzHeader рендерится в App.jsx (постоянная шапка) — здесь не нужен.
          minHeight = вьюпорт минус шапка (шапка теперь снаружи, над этим div). */}
      {/* v32.16.24: row on desktop, single column on mobile so the chat panel gets
          the screen instead of being squeezed next to the fixed 280px sidebar. */}
      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? 10 : 16, padding: mobile ? 10 : 16, maxWidth: 1500, margin: '0 auto', alignItems: 'stretch', height: mobile ? 'calc(100dvh - 56px)' : 'calc(100vh - 64px)' }}>

        {/* Sidebar */}
        <div style={{ width: mobile ? '100%' : 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: mobile ? 8 : 12 }}>
          <button onClick={newChat} style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, color: '#fff', background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)', boxShadow: '0 4px 16px rgba(168,85,247,0.35)' }}>＋ New Chat</button>

          {/* v32.16.24: on mobile the history list is capped (flex:none + maxHeight) so it
              stays compact at the top and the chat panel below keeps most of the height. */}
          <div style={{ ...panel, flex: mobile ? 'none' : 1, minHeight: 0, maxHeight: mobile ? 150 : undefined, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94A3B8', borderBottom: '1px solid rgba(99,102,241,0.14)' }}>History</div>
            <div style={{ overflowY: 'auto', flex: 1, padding: 6 }}>
              {chats.length === 0 && <div style={{ padding: 14, color: '#64748B', fontSize: 13 }}>No conversations yet.</div>}
              {chats.map(c => (
                <div key={c.id} onClick={() => setActiveId(c.id)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '9px 10px', margin: '2px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                    background: c.id === activeId ? 'rgba(168,85,247,0.16)' : 'transparent',
                    border: `1px solid ${c.id === activeId ? 'rgba(168,85,247,0.4)' : 'transparent'}`, color: c.id === activeId ? '#E9D5FF' : '#CBD5E1' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || 'Untitled'}</span>
                  <span onClick={(ev) => { ev.stopPropagation(); const next = chats.filter(x => x.id !== c.id); persist(next); if (activeId === c.id) setActiveId(next[0] ? next[0].id : null); }}
                    title="Delete" style={{ color: '#64748B', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</span>
                </div>
              ))}
            </div>
          </div>

          {/* v32.16.24: Knowledge Base (file upload) hidden on mobile — uploading CSV/Excel
              is a desktop task and hiding it gives the chat the vertical space it needs. */}
          <div style={{ ...panel, display: mobile ? 'none' : 'block' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(99,102,241,0.14)' }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94A3B8' }}>Knowledge Base</span>
              <button onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={uploading}
                style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 7, cursor: uploading ? 'default' : 'pointer', border: '1px solid rgba(168,85,247,0.4)', background: 'rgba(168,85,247,0.14)', color: '#C4B5FD' }}>
                {uploading ? 'Uploading…' : '↥ Upload'}</button>
              <input ref={fileInputRef} type="file" accept=".csv,.txt,.json,.xlsx,.xls" onChange={onUploadPick} style={{ display: 'none' }} />
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto', padding: 6 }}>
              {filesError && <div style={{ padding: 10, color: '#F87171', fontSize: 12 }}>{filesError}</div>}
              {!filesError && files.length === 0 && <div style={{ padding: 10, color: '#64748B', fontSize: 12 }}>No files uploaded. Add CSV / TXT / JSON / Excel for the advisor to use.</div>}
              {files.map((f, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 8px', fontSize: 12, color: '#CBD5E1' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {f.name}</span>
                  <span style={{ color: '#64748B', flexShrink: 0 }}>{fmtSize(f.size)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chat area */}
        <div style={{ ...panel, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(99,102,241,0.14)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🤖</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#F1F5F9' }}>AI Traffic Advisor</div>
              <div style={{ fontSize: 12, color: '#94A3B8' }}>Geo · Affiliate · Broker performance, powered by your live data</div>
            </div>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {messages.length === 0 && (
              <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 520, color: '#94A3B8' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🏴‍☠️</div>
                <div style={{ fontSize: 15, lineHeight: 1.5 }}>Ask me anything about your traffic — conversion rates by geo, which affiliates are pulling their weight, where to route next, or what to cut. Pick a quick action below or just type.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '80%', padding: '11px 15px', borderRadius: 14, fontSize: 14, lineHeight: 1.5,
                  background: m.role === 'user' ? 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)' : 'rgba(30,41,59,0.85)',
                  color: m.role === 'user' ? '#fff' : '#E2E8F0',
                  border: m.role === 'user' ? 'none' : '1px solid rgba(99,102,241,0.18)',
                  borderBottomRightRadius: m.role === 'user' ? 4 : 14, borderBottomLeftRadius: m.role === 'user' ? 14 : 4 }}
                  dangerouslySetInnerHTML={{ __html: aiRenderMarkdown(m.content) }} />
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '11px 16px', borderRadius: 14, background: 'rgba(30,41,59,0.85)', border: '1px solid rgba(99,102,241,0.18)', color: '#A855F7', fontSize: 14 }}>
                  <span style={{ fontStyle: 'italic' }}>Captain Jack is charting the waters…</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div style={{ display: 'flex', gap: 8, padding: '0 16px 10px', flexWrap: 'wrap' }}>
            {QUICK_ACTIONS.map((q, i) => (
              <button key={i} onClick={() => send(q.prompt)} disabled={loading}
                style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 12px', borderRadius: 9, cursor: loading ? 'default' : 'pointer',
                  border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.1)', color: '#C7D2FE', opacity: loading ? 0.5 : 1 }}>
                {q.icon} {q.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div style={{ display: 'flex', gap: 10, padding: 16, borderTop: '1px solid rgba(99,102,241,0.14)' }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about CR%, profit, routing, low performers…  (Enter to send, Shift+Enter for a new line)"
              rows={1}
              style={{ flex: 1, resize: 'none', maxHeight: 120, padding: '12px 14px', borderRadius: 10, fontSize: 14, lineHeight: 1.4,
                background: 'rgba(15,20,38,0.9)', border: '1px solid rgba(99,102,241,0.3)', color: '#E2E8F0', outline: 'none', fontFamily: 'inherit' }} />
            <button onClick={() => send()} disabled={loading || !input.trim()}
              style={{ padding: '0 22px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 14, color: '#fff',
                cursor: (loading || !input.trim()) ? 'default' : 'pointer',
                background: (loading || !input.trim()) ? 'rgba(99,102,241,0.35)' : 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' }}>
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Pages forced to render in their NATIVE LIGHT design for ALL users, regardless
// of the global dark theme. Dark theme is an overlay (darkModeCSS) applied via
// the `.dark-mode` class on the root wrapper; for these pages we simply DROP that
// class (see useLayoutEffect in AppInner) so the page shows its original light
// design with zero per-color CSS reset. "Offers" = page key 'deals' (per the
// BlitzHeader nav label). Mirrors AutoB2026's "always white" intent, done
// structurally instead of via CSS `unset`.
// 2026-10-08 'dailycalcs' added at the owner's request: redesigned DC
// (balances, registry, table) is built from light mockups; under the default
// dark theme it rendered half-dark (dark cells around white inputs).
const FORCE_LIGHT_PAGES = ["deals", "partners", "roster", "dailycalcs"];

function AppInner() {
  // FIX 2026-06-04: AppInner referenced a bare `addToast` (handleRefresh / revert / data-sync
  // toasts) without ever declaring it → dormant "Can't find variable: addToast" ReferenceError
  // whenever Refresh or Revert was clicked. ToastProvider wraps AppInner (App() render tree), so
  // useToast() resolves here. One declaration repairs every addToast(...) call site in AppInner.
  const { addToast } = useToast();
  const [user, setUser] = useState(() => {
    const session = getSession();
    // Restore server session token on page reload
    const _tok=(session&&session.token)||localStorage.getItem('blitz_token'); if(_tok)setSessionToken(_tok);
    return session ? { email: session.email, name: session.name, pageAccess: session.pageAccess, cpDesign: session.cpDesign, paymentsDesign: session.paymentsDesign, genBalancesDesign: session.genBalancesDesign } : null;
  });
  const [authRev, setAuthRev] = useState(0);

  // Logout when server rejects our token (401)
  useEffect(() => {
    const handle401 = () => { clearSession(); setUser(null); };
    window.addEventListener('blitz_401', handle401);
    return () => window.removeEventListener('blitz_401', handle401);
  }, []);

  // 2026-08-31 (Phase 1 session TTL, П2) — the local-TTL check (getSession()'s 23h
  // threshold, extracted to session-expiry.js) used to run ONCE, on boot. A session that
  // died while the tab sat open/idle was invisible until the next reactive 401 — which
  // never fires for a tab that isn't sending anything (Sophia incident: offline tab,
  // stale-dead session, no 401 possible because the request never left). Re-run the SAME
  // check on visibility return / focus / a 60s heartbeat; checkLocalSessionExpiry() itself
  // stays a no-op (no network) whenever the local session isn't expired, so the interval
  // is effectively free while the session is alive.
  // 2026-08-31 (Phase 2 «Офлайн видим», П4) — probeConnection riding the SAME
  // visible/focus/60s triggers as checkLocalSessionExpiry above (this is a
  // DIFFERENT verdict — offline connectivity, not session TTL — checkLocalSessionExpiry
  // itself is untouched). Unconditional on both visible and focus: no WS/serverOnline
  // check first, because a zombie WS (readyState still OPEN while nothing real is on
  // the other end) is exactly the blindness this phase exists to close — probeConnection's
  // own cooldown (3s) absorbs a tab-switch flicker, no extra guard needed here. The
  // 60s tick stays visibility-gated (only probes while `visible`) — someone actively
  // typing in the tab never fires visible/focus at all, so this is their only watchdog;
  // hidden tabs don't need it (they're not looking at anything).
  useEffect(() => {
    const stop = startHealthWatch();
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      checkLocalSessionExpiry('visible');
      probeConnection();
    };
    const onFocus = () => { checkLocalSessionExpiry('focus'); probeConnection(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    const interval = setInterval(() => {
      checkLocalSessionExpiry('interval');
      if (document.visibilityState === 'visible') probeConnection();
    }, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
      stop();
    };
  }, []);

  // 2026-08-31 (Phase 1 session TTL, П4; fix-round FR4 — cursor#2 + жюри A#3) — cross-tab:
  // getSessionToken() (utils/session.js) caches the token in module memory, only
  // re-reading localStorage when the cache is null. `storage` never fires in the tab that
  // made the change — only in OTHER tabs — so this is purely a cross-tab signal, by
  // construction.
  //
  // FR4 fixed two problems in the original (round-1) version:
  //  1. It reacted to ANY `e.newValue` truthy write, including the SAME token being
  //     rewritten (e.g. restoreKilledSession re-writing the identical token after a false
  //     alarm) — now requires a REAL change (`e.newValue !== e.oldValue`).
  //  2. It cleared THIS tab's own CONFIRMED-dead verdict (two real 401s, decided locally)
  //     on any cross-tab token write — a stranger's token write is not grounds to erase a
  //     verdict this tab already confirmed. Now storage NEVER touches the confirmed-dead
  //     modal (resetSessionExpiredNotice/SESSION_EXPIRED_CLEARED_EVENT removed from this
  //     path entirely) — only a real onLogin in THIS tab clears that, same as always.
  //     sessionEpoch is untouched by this listener as a result (jury A: SESSION_VERDICT_
  //     STALE risk from bumping epoch out from under an in-flight probe).
  // Storage only ever clears local SUSPICION (offline guess, not a confirmed verdict) +
  // busts the token cache. newValue === null (logout elsewhere): cache-bust only, as
  // before. The "tab A cleared the shared token while tab B's session is still live" race
  // (critique F7) remains a known, unfixed gap for this phase.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'blitz_token') return;
      resetSessionTokenCache();
      if (e.newValue !== e.oldValue) {
        if (e.newValue) clearLocalSuspicion();
        connectWebSocket();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const handleToast = ({ detail }) => {
      if (typeof detail?.message !== 'string' || !detail.message.trim()) return;
      addToast(detail.message, detail.type);
    };
    window.addEventListener(TOAST_EVENT, handleToast);
    return () => window.removeEventListener(TOAST_EVENT, handleToast);
  }, [addToast]);

  // Preload of 14 lazy page chunks moved to module-level (top of App.jsx).
  // Fires at boot, before AppInner mounts — beats user navigation reliably.
  // Overview is now eager (in main bundle), so no preload needed for it.

  const [users, setUsers] = useState(() => lsGet('users', null) || []);
  const [_payments, _setPayments] = useState(() => lsGet('payments', null) || []);
  const [_cpPayments, _setCpPayments] = useState(() => lsGet('customer-payments', null) || []);
  // Safe wrappers: NEVER allow state to become null/undefined (prevents crash on empty delete)
  const payments = Array.isArray(_payments) ? _payments : [];
  const cpPayments = Array.isArray(_cpPayments) ? _cpPayments : [];
  const setPayments = useCallback((v) => _setPayments(prev => { const a = Array.isArray(prev) ? prev : []; const n = typeof v === 'function' ? v(a) : v; return Array.isArray(n) ? n : a; }), []);
  const setCpPayments = useCallback((v) => _setCpPayments(prev => { const a = Array.isArray(prev) ? prev : []; const n = typeof v === 'function' ? v(a) : v; return Array.isArray(n) ? n : a; }), []);
  const [crgDeals, setCrgDeals] = useState(() => lsGet('crg-deals', null) || []);
  // v32.17.x Daily Cap v2 Этап 3: настройки аффилиат-капов. Simple array state,
  // загружается при старте параллельно с другими таблицами через SIMPLE_ENDPOINT-паттерн.
  // При WS broadcastUpdate для 'daily-cap-aff-caps' → setter заменяет массив целиком.
  const [affCaps, setAffCaps] = useState([]);

  // v31.18: dcEntries больше НЕ persistent state — computed live from crgDeals.
  // Почему: user-requested rewrite 2026-04-20. Раньше дублировалось в daily-cap.json
  // (+ manual edits), что приводило к постоянным рассинхронам (CRG total ≠ DC total).
  // Теперь single source of truth = crgDeals. Формула идентична прежней syncFromCRG:
  //   deal.manageAff + cap → agent.affiliates
  //   deal.madeSale  + cap → agent.brands
  // Нормализация имён через normalizeAgent (Kate→Katie, Oleksandra→Alex, etc).
  // setDcEntries остаётся no-op чтобы старые callsites (WS conflict, startup merge,
  // handleRefresh, revert) не ломались — они просто ничего не делают, dcEntries
  // обновится само при следующем recompute от crgDeals change.
  // v32.17.x Daily Cap v2 Этап 3: гибридная агрегация Affiliates.
  // Правило per-(date, agent):
  //   - Есть запись в affCaps → Affiliates = Σ affs[].cap (из настроек)
  //   - Нет записи → Affiliates = Σ deal.cap по manageAff (CRG fallback)
  // Brands: всегда из madeSale (CRG), без изменений.
  // Overrides (daily-cap-overrides) накладываются ПОВЕРХ в DailyCap/index.jsx — не здесь.
  // Чистая логика вынесена в src/lib/computeDcAffiliates.js для unit-тестирования.
  // D1 (2026-08-03): рабочая выборка CRG — всё, кроме черновиков парсера
  // (status='draft'). Черновик — это строка, в которой LLM-парсер сам не уверен;
  // до нажатия «Принять» она не ротация, а вопрос, и не должна попадать ни в
  // капы, ни в статистику, ни в KPI. Полный массив (с черновиками) получает
  // ТОЛЬКО страница CRG Deals — там их и разбирают.
  const crgDealsLive = React.useMemo(
    () => (crgDeals || []).filter(d => d && d.status !== 'draft'),
    [crgDeals],
  );

  const dcEntries = React.useMemo(() => {
    return computeDcAffiliates(crgDealsLive, affCaps, normalizeAgent);
  }, [crgDealsLive, affCaps]);
  // No-op setter — preserved for backward compatibility с существующими callsites.
  // Любой setDcEntries(...) = молча игнорируется. dcEntries computed, не persistent.
  const setDcEntries = useCallback(() => {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Daily Cap] setDcEntries called — ignored (computed from crgDeals)');
    }
  }, []);

  const [dealsData, setDealsData] = useState(() => lsGet('deals', null) || []);
  const [offersRaw, setOffersRaw] = useState(() => lsGet('offers', null) || []);
  const [walletsData, setWalletsData] = useState(() => lsGet('wallets', null) || []);
  const [partnersData, setPartnersData] = useState(() => lsGet('partners', null) || []);
  const [confFoldersData, setConfFoldersData] = useState(() => lsGet('conference-folders', null) || []);
  const [confPartnersData, setConfPartnersData] = useState(() => lsGet('conference-partners', null) || []);
  const [sellingOffers, setSellingOffers] = useState(() => lsGet('selling-offers', null) || []);
  const [ftdEntries, setFtdEntries] = useState(() => lsGet('ftd-entries', null) || []);
  const [dailyCalcs, setDailyCalcs] = useState(() => lsGet('daily-calcs-data', null) || []);
  const [page, setPage] = useState("overview");
  const [loaded, setLoaded] = useState(false);

  // ── Force-light pages ────────────────────────────────────────────────────
  // Drop the root `.dark-mode` class while Offers (deals) / Partners /
  // Affiliates & Brokers (roster) are active so they always render in their
  // native light design for every user — even ones on the (default) dark theme.
  // useLayoutEffect runs BEFORE paint → no dark→light flash on navigation.
  // The class lives on the same root <div> as before (id="blitz-theme-root"),
  // so .dark-mode consumers (frost color, theme observer) behave identically.
  const { dark, toggle } = useTheme();
  useLayoutEffect(() => {
    const root = document.getElementById("blitz-theme-root");
    if (!root) return;
    root.className = (dark && !FORCE_LIGHT_PAGES.includes(page)) ? "dark-mode" : "";
  }, [dark, page]);
  // Two-mechanism force-light (the dark theme has TWO independent paths):
  //   1) overlay pages (Partners, Offers/deals) darken via the root `.dark-mode`
  //      class → handled above by dropping that class.
  //   2) self-theming pages (Roster) read useTheme().dark directly and add their
  //      OWN `.rp-dark` class — the root class doesn't touch them. So we ALSO
  //      override the theme context to dark:false for force-light pages (see the
  //      ThemeContext.Provider wrap on the deals/partners/roster routes below).
  // `forceLightCtx` is that override value, reused across the three routes.
  const forceLightCtx = { dark: false, toggle };

  // PR-PERSONAL-2-FIX10 2026-05-24: TWO-LAYER architecture for readability.
  //
  // Previous fixes: cards с rgba bg или no bg рендеру через transparent ancestors
  // → daisies show через text → unreadable. Need readability layer.
  //
  // FIX10 architecture (3 stacked layers):
  //   z-index -2: bg image layer (slider controls opacity)
  //   z-index -1: frost overlay (semi-transparent white/dark, theme-aware)
  //                → mutes bg, makes content readable
  //   z-index default: content (page wrappers transparent via CSS)
  //
  // Result: bg visible as proper wallpaper, frost layer мutes intensity,
  // content (including semi-transparent cards) remains readable.
  // Frost color adapts к theme — white in light mode, dark в dark mode.
  useEffect(() => {
    const removeBg = () => {
      const layer = document.getElementById('user-bg-layer');
      if (layer) layer.remove();
      const frost = document.getElementById('user-bg-frost');
      if (frost) frost.remove();
      const styles = document.getElementById('user-bg-styles');
      if (styles) styles.remove();
    };

    if (!user?.email) {
      removeBg();
      return removeBg;
    }

    let activeBlobUrl = null;
    // 2026-09-28: fetchAndApply — «выстрелил и забыл», и без отмены поздний ответ трогал
    // DOM уже после cleanup: на логауте вставлял фон обратно на экран логина (blob-URL
    // при этом никто не освобождал), а в тестах просыпался после сноса jsdom — catch звал
    // removeBg → «document is not defined» → unhandled rejection роняла весь прогон кодом 1.
    // Тот же класс, что загрузочная цепочка ниже (2026-09-05): проверка после каждого await.
    let cancelled = false;

    const getOpacity = () => {
      try {
        const v = parseFloat(localStorage.getItem('blitz_bg_opacity'));
        return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0.6;
      } catch { return 0.6; }
    };

    // FIX10: theme-aware frost color. Reads .dark-mode class.
    const getFrostColor = () => {
      const isDark = document.querySelector('.dark-mode') !== null;
      return isDark ? 'rgba(15, 23, 42, 0.55)' : 'rgba(255, 255, 255, 0.55)';
    };

    const ensureLayer = () => {
      // PR-PERSONAL-2-FIX12 2026-05-24: AGGRESSIVE bg coverage.
      // FIX11 only caught cards с border-radius — KpiStrip + filter chips + title
      // sections без border-radius stayed transparent → daisies через text.
      //
      // FIX12: wrap entire <main> + every direct child of main в solid bg.
      // Pages keep header (BlitzHeader) opaque already. Main content gets uniform
      // solid bg. Bg image visible через app shell padding + between main blocks.
      if (!document.getElementById('user-bg-styles')) {
        const styleEl = document.createElement('style');
        styleEl.id = 'user-bg-styles';
        styleEl.textContent = `
          /* Layer 1: body + html + scroll container transparent */
          html, body { background: transparent !important; }
          body > #root { background: transparent !important; }
          body > #root > div { background: transparent !important; background-image: none !important; }

          /* Layer 2: page outer wrappers transparent (min-height: 100vh fingerprint) */
          body div[style*="min-height: 100vh"],
          body div[style*="min-height:100vh"],
          body div[style*="minHeight: 100vh"],
          body div[style*="minHeight:100vh"] {
            background: transparent !important;
            background-color: transparent !important;
            background-image: none !important;
          }

          /* Layer 3: aggressive bg coverage — wrap entire <main> + all section blocks.
             Light theme = white background, dark theme = dark navy.
             Uses 0.96 opacity для slight bg bleed (glass-morphism hint). */
          body main {
            background-color: rgba(255, 255, 255, 0.96) !important;
            border-radius: 16px !important;
            margin: 16px auto !important;
            padding: 24px !important;
          }
          .dark-mode main {
            background-color: rgba(15, 23, 42, 0.96) !important;
          }

          /* Catch all cards с standard border-radius values for cases where main
             is not used (e.g., pages structured без semantic <main>). */
          body div[style*="border-radius: 8px"],
          body div[style*="border-radius:8px"],
          body div[style*="border-radius: 10px"],
          body div[style*="border-radius:10px"],
          body div[style*="border-radius: 12px"],
          body div[style*="border-radius:12px"],
          body div[style*="border-radius: 14px"],
          body div[style*="border-radius:14px"],
          body div[style*="border-radius: 16px"],
          body div[style*="border-radius:16px"],
          body div[style*="border-radius: 18px"],
          body div[style*="border-radius:18px"],
          body div[style*="border-radius: 20px"],
          body div[style*="border-radius:20px"] {
            background-color: rgba(255, 255, 255, 0.97) !important;
          }
          .dark-mode div[style*="border-radius: 8px"],
          .dark-mode div[style*="border-radius:8px"],
          .dark-mode div[style*="border-radius: 10px"],
          .dark-mode div[style*="border-radius:10px"],
          .dark-mode div[style*="border-radius: 12px"],
          .dark-mode div[style*="border-radius:12px"],
          .dark-mode div[style*="border-radius: 14px"],
          .dark-mode div[style*="border-radius:14px"],
          .dark-mode div[style*="border-radius: 16px"],
          .dark-mode div[style*="border-radius:16px"],
          .dark-mode div[style*="border-radius: 18px"],
          .dark-mode div[style*="border-radius:18px"],
          .dark-mode div[style*="border-radius: 20px"],
          .dark-mode div[style*="border-radius:20px"] {
            background-color: rgba(15, 23, 42, 0.97) !important;
          }
        `;
        document.head.appendChild(styleEl);
      }

      // FIX11: bg image layer at z-index -1 (behind content, NO frost — cards have own bg)
      let layer = document.getElementById('user-bg-layer');
      if (!layer) {
        layer = document.createElement('div');
        layer.id = 'user-bg-layer';
        Object.assign(layer.style, {
          position: 'fixed',
          top: '0', left: '0', right: '0', bottom: '0',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          pointerEvents: 'none',
          zIndex: '-1',  // behind content
          opacity: String(getOpacity()),
          transition: 'opacity 150ms ease',
        });
        document.body.insertBefore(layer, document.body.firstChild);
      }

      return layer;
    };

    // FIX11: no theme observer needed (no frost layer); kept stub variable so
    // existing cleanup line `themeObserver.disconnect()` doesn't throw.
    const themeObserver = { observe: () => {}, disconnect: () => {} };

    const fetchAndApply = async () => {
      try {
        const res = await fetch(`${API_BASE}/user-background`, { headers: authHeaders() });
        if (cancelled) return;
        if (!res.ok) {
          removeBg();
          if (activeBlobUrl) { URL.revokeObjectURL(activeBlobUrl); activeBlobUrl = null; }
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        const layer = ensureLayer();
        layer.style.backgroundImage = `url("${url}")`;
        if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);
        activeBlobUrl = url;
      } catch (_) {
        if (cancelled) return;
        removeBg();
      }
    };

    fetchAndApply();

    // Watch for theme toggle (.dark-mode class on body OR ancestor)
    themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Settings page fires событие after upload/remove + slider change.
    const onBackgroundUpdated = () => fetchAndApply();
    const onOpacityChange = (e) => {
      const layer = document.getElementById('user-bg-layer');
      if (layer && typeof e.detail?.opacity === 'number') {
        layer.style.opacity = String(e.detail.opacity);
      }
    };
    window.addEventListener('user-background-updated', onBackgroundUpdated);
    window.addEventListener('user-background-opacity-changed', onOpacityChange);

    return () => {
      cancelled = true;
      themeObserver.disconnect();
      window.removeEventListener('user-background-updated', onBackgroundUpdated);
      window.removeEventListener('user-background-opacity-changed', onOpacityChange);
      if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);
      removeBg();
    };
  }, [user?.email]);

  // PR-PERSONAL-2-FIX5: hide bg layer на data-heavy pages для table readability.
  useEffect(() => {
    const BG_BLACKLIST_PAGES = new Set(['payments', 'customers', 'deals', 'ftdsinfo', 'genbalances', 'invoicegen']);
    const layer = document.getElementById('user-bg-layer');
    if (!layer) return;
    layer.style.display = BG_BLACKLIST_PAGES.has(page) ? 'none' : 'block';
  }, [page]);

  // PR-PERSONAL-2-FIX4: removed body.dataset.page sync — blacklist now handled
  // в JS via the bg layer's useEffect above (display:none when page in blacklist).

  // ── Undo / Revert history ──────────────────────────────────────
  // Each entry: { table, data, label, ts, meta }  — max 1 entry per table (last change only).
  // `meta` is optional and opaque here: a page that needs more than the whole-table snapshot
  // to undo honestly (Payments: which row, what it held before) passes it and reads it back
  // in its onRevert.
  const undoHistory = useRef({});
  const pushUndo = React.useCallback((table, prevData, label, meta) => {
    if (!Array.isArray(prevData) || prevData.length === 0) return;
    undoHistory.current[table] = { data: prevData, label, ts: serverNow(), meta };
  }, []);
  const popUndo = React.useCallback((table) => {
    const entry = undoHistory.current[table];
    if (!entry) return null;
    delete undoHistory.current[table];
    return entry;
  }, []);
  const hasUndo = (table) => !!undoHistory.current[table];
  // A page with its own honest ↩ Revert registers it here while it is open, so the header's ↩
  // (and the mobile menu's) runs that one instead of re-sending an old snapshot (2026-09-23).
  const pageReverts = useRef({});
  const registerRevert = React.useCallback((table, onRevert) => {
    pageReverts.current[table] = onRevert;
    return () => { delete pageReverts.current[table]; };
  }, []);

  // Keep TEAM_NAMES in sync with User Management ──
  useEffect(() => {
    if (users && users.length > 0) {
      const names = users.map(u => u.name).filter(Boolean);
      if (names.length > 0) TEAM_NAMES_REF.current = names;
      // user_colors фича (2026-07-13, §2.2 брифа): тот же эффект, та же зависимость
      // [users] — WS-push таблицы users уже перерисовывает всё дерево (так же, как
      // работают имена), отдельный механизм не нужен. Юзеры без своего цвета
      // (u.color отсутствует) просто не попадают в Map → getPersonColor падает
      // в легаси PEOPLE_COLORS/hash для них, как и раньше. Фильтр и по u.name
      // (jury 2026-07-13 §9): запись с color, но без name дала бы Map-ключ
      // undefined/'' — зеркалим смысл names.filter(Boolean) строкой выше.
      USER_COLORS_REF.current = new Map(users.filter(u => u.color && u.name).map(u => [u.name, u.color]));
    }
  }, [users]);

  // 2026-04-18: Re-hydrate current user's pageAccess/name from fresh users[] array.
  // Bug: когда admin менял pageAccess юзера через UI — серверная users.json обновлялась,
  // WS пушил new users[] array, setUsers() обновлял state для admin UI. Но current `user`
  // state (залогиненного юзера) остаётся с pageAccess **с момента login** — stale. Юзер
  // видит только Dashboard + Settings (fallback из getPageAccess когда pageAccess пустой
  // или old), хотя в users.json admin видит у него access к 9 вкладкам.
  // Fix: watch users[] changes — если сервер прислал моего юзера с new pageAccess/name,
  // обновить user state + saveSession чтобы localStorage был up-to-date.
  // FIX 2: placed AFTER `const [users, setUsers]` declaration (line 11078). Ранее был выше,
  // что вызывало TDZ crash ("Cannot access 'r' before initialization") — минифицированный
  // `users` referenced до initialization. Now это после declaration, safe.
  useEffect(() => {
    // 2026-04-19: defensive guards — skip rehydrate в unstable initial states
    // (до loaded=true) чтобы избежать race conditions во время login transition.
    // Также require mine.pageAccess быть valid array перед сравнением.
    if (!loaded) return;
    if (!user?.email || !Array.isArray(users) || users.length === 0) return;
    const mine = users.find(u => u.email === user.email);
    if (!mine || !Array.isArray(mine.pageAccess)) return;
    const needUpdate =
      JSON.stringify(user.pageAccess || []) !== JSON.stringify(mine.pageAccess) ||
      user.name !== mine.name;
    if (needUpdate) {
      const updated = { ...user, pageAccess: mine.pageAccess, name: mine.name };
      setUser(updated);
      try { saveSession(updated); } catch (_) {}
    }
  }, [users, loaded]);  // eslint-disable-line react-hooks/exhaustive-deps
  const skipSave = useRef(true);
  const pollInFlightRef = useRef(false);
  const serverFetchDone = useRef(false); // CRITICAL: block saves until first server fetch completes

  // ═══════════════════════════════════════════════════════════════
  // SYNC ENGINE v3.26 — LOCAL-FIRST WITH ID-BASED MERGE
  // ═══════════════════════════════════════════════════════════════
  // GOLDEN RULE: NEVER replace local state with server data. ALWAYS merge by id.
  // The mergeByID engine now lives in src/lib/merge-by-id.js (C1 extract
  // 2026-05-29) — imported above; near-pure (no AppInner closure dep), registered
  // for the global 409-conflict path via setMergeByIDGlobal(mergeByID) below.
  // ═══════════════════════════════════════════════════════════════

  // Expose mergeByID for global conflict resolution + register all setters
  setMergeByIDGlobal(mergeByID);
  registerConflictSetter('users', setUsers);
  registerConflictSetter('payments', setPayments);
  registerConflictSetter('customer-payments', setCpPayments);
  registerConflictSetter('crg-deals', setCrgDeals);
  registerConflictSetter('daily-cap', setDcEntries);
  registerConflictSetter('deals', setDealsData);
  registerConflictSetter('wallets', setWalletsData);
  registerConflictSetter('partners', setPartnersData);
  registerConflictSetter('conference-folders', setConfFoldersData);
  registerConflictSetter('conference-partners', setConfPartnersData);
  registerConflictSetter('selling-offers', setSellingOffers);
  registerConflictSetter('ftd-entries', setFtdEntries);
  registerConflictSetter('daily-calcs-data', setDailyCalcs);

  useEffect(() => {
    // 2026-09-05: загрузочная цепочка — это async-IIFE «выстрелил и забыл», и у неё
    // было две дыры.
    //   1) Промис не отдан никому: любая ошибка внутри становится unhandled rejection.
    //      В браузере она видна только в консоли/Sentry; в тестовом прогоне роняет весь
    //      прогон кодом 1 при зелёных тестах (CI run 33812376227: цепочка проснулась
    //      после сноса jsdom и позвала getSessionToken → «localStorage is not defined»).
    //   2) Цепочка не останавливалась ни на unmount, ни на смене пользователя. Пять
    //      точек await — это пять мест, где вкладка успевает закрыться или человек
    //      успевает войти; после них старый прогон продолжал писать состояние.
    // Плюс таймеры снятия замка сохранения не отменялись: таймер СТАРОГО прогона мог
    // снять замок посреди НОВОЙ загрузки (эффект перезапускается на смене user) —
    // автосейв уезжал бы поверх ещё не догруженных данных.
    let cancelled = false;
    const lockTimers = [];
    const releaseSaveLockAfter = (delayMs, log) => {
      lockTimers.push(setTimeout(() => {
        if (cancelled) return;
        releaseBootSkipSave(skipSave);
        if (log) console.log(log);
      }, delayMs));
    };
    (async () => {
      holdBootSkipSave(skipSave);

      // Step 0: Check server health
      try {
        const healthRes = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(2000) }); // FIX: 2s (was 4s) — faster offline detection on mobile
        if (cancelled) return;
        if (healthRes.ok) setServerOnline(true);
      } catch {
        // Проверка отмены нужна в ОБЕИХ ветках: setServerOnline — общий модульный
        // стейт соединения, он трогает сессию и рассылает подписчикам, и делать это
        // от имени уже закрытой вкладки нельзя.
        if (cancelled) return;
        setServerOnline(false);
      }

      const hasToken = !!getSessionToken();

      // Step 1: ALWAYS load localStorage FIRST (local-first — instant paint)
      const local = {
        users: lsGet('users', null), payments: lsGet('payments', null),
        'customer-payments': lsGet('customer-payments', null), 'crg-deals': lsGet('crg-deals', null),
        'daily-cap': lsGet('daily-cap', null), deals: lsGet('deals', null), wallets: lsGet('wallets', null),
        // v11.02 L1+L2: added missing tables so offline edits survive first load
        partners: lsGet('partners', null), 'conference-folders': lsGet('conference-folders', null), 'conference-partners': lsGet('conference-partners', null), 'ftd-entries': lsGet('ftd-entries', null), 'daily-calcs-data': lsGet('daily-calcs-data', null),
      };
      // v31.62: localStorage hydration — strip telegram fields (defense-in-depth).
      // Server is single source of truth для telegram fields. Cached localStorage может
      // быть stale (browser was offline когда server preserved value, или admin loaded
      // older bundle). Frontend admin UI не должен оперировать stale telegram data.
      // Server fetch (Step 2 ниже) replaces stripped local data with authoritative server state.
      if (local.users && local.users.length > 0) {
        const stripped = local.users.map(u => {
          const { telegramChatId, telegramUsername, ...rest } = u;
          return rest;
        });
        setUsers(stripped);
      }
      if (Array.isArray(local.payments)) setPayments(local.payments);
      if (Array.isArray(local['customer-payments'])) setCpPayments(local['customer-payments']);
      if (Array.isArray(local['crg-deals'])) setCrgDeals(local['crg-deals']);
      if (Array.isArray(local['daily-cap'])) setDcEntries(local['daily-cap']);
      if (Array.isArray(local.deals)) setDealsData(local.deals);
      if (Array.isArray(local.wallets)) setWalletsData(local.wallets);

      // FIX: Show app immediately from localStorage — don't block on server fetch
      // On slow Android connections this was causing 20+ second hang on splash screen
      // Server fetch now happens in background AFTER the app is already displayed
      setLoaded(true);
      serverFetchDone.current = true;

      // ═══════════════════════════════════════════════════════════════════════
      // v31.66: Step 1.5 — SERVER-AUTHORITATIVE SESSION PROBE
      // ═══════════════════════════════════════════════════════════════════════
      // Closes H1 + H2 cascade от session_lifecycle audit:
      //  H1: frontend думал session valid 7d, backend evicted at 24h → mismatch
      //  H2: один из 11 параллельных apiGet получает 401 → sessionExpiredFlag set
      //      → mass setUser(null) → "залогинен и через 2s выкинуло"
      //
      // Resolution: ОДИН decisive probe к /api/session ДО Step 2 fetch storm.
      //   probe 401          → session реально invalid → clean logout (без 11-fetch шторма)
      //   probe 200          → session valid → refresh user state с authoritative pageAccess
      //   probe network fail → assume offline, gracefully degrade к local-first behavior
      //
      // v31.66 AI review feedback applied:
      //  M3: Probe AFTER setLoaded(true) — splash instant from localStorage, не блокирует render.
      //      Trade-off: если probe 401, user видит main UI ~100ms потом flash к LoginScreen.
      //      Acceptable: лучше slight flash чем 6s frozen splash на slow mobile.
      //  A1: Skip probe в justLoggedIn window — fresh login token гарантированно known к backend
      //      (just set в same request). Probing redundant + eliminates infinite-loop architectural
      //      risk (если probe always 401 для some reason, login → probe → logout → login → loop).
      //  M1: Sort pageAccess в compare (idempotent) — eliminates spurious re-renders если backend
      //      возвращает same items в different order.
      //
      // Bonus: pageAccess refresh — admin может было изменил permissions пока user был offline.
      // Probe возвращает up-to-date data, user видит latest без re-login.
      const tokenAtProbe = getSessionToken();
      if (tokenAtProbe && serverOnline && !justLoggedIn) {
        try {
          const probeRes = await fetch(`${API_BASE}/session`, {
            headers: authHeaders(),
            signal: AbortSignal.timeout(5000),
          });
          // v31.66 decision extracted to classifySessionProbe (unit-tested). Body is read
          // only when the response was ok — same as before (401/5xx never reached json()).
          const probeBody = probeRes.ok ? await probeRes.json() : null;
          if (cancelled) return;
          const decision = classifySessionProbe(probeRes.status, probeBody);
          if (decision.action === 'logout') {
            // Authoritative answer от backend — session реально invalid (expired или revoked)
            console.warn('🔴 SESSION_PROBE_401: server-authoritative reject — clean logout');
            // 2026-08-18: человека и так ведут на экран входа — модалка «Session expired»
            // поверх формы входа была бы лишней.
            suppressSessionExpiredNotice();
            setSessionToken(null);
            localStorage.removeItem('blitz_session');
            setUser(null);
            releaseBootSkipSave(skipSave);
            return;  // setLoaded already called above
          }
          if (decision.action === 'refresh') {
            const freshUser = decision.user;
            // CP redesign 2026-06-21: cpDesign is an OPTIMISTIC, locally-owned preference set by
            // the design switcher via setUser — which (this effect deps on `user`) re-runs THIS
            // effect and fires a probe. A probe racing the still-in-flight POST /api/user-cp-design
            // carries a STALE cpDesign and would flip the page back. Preserve the local choice so
            // it never reverts; pageAccess/name remain server-authoritative.
            const reconciled = { ...freshUser, cpDesign: user?.cpDesign ?? freshUser.cpDesign, paymentsDesign: user?.paymentsDesign ?? freshUser.paymentsDesign, genBalancesDesign: user?.genBalancesDesign ?? freshUser.genBalancesDesign };
            // v31.66: refresh user state ТОЛЬКО если actually changed (избегаем re-render
            // loop — useState reference equality skip если return same ref). Sort pageAccess
            // для idempotent compare (M1 fix — backend may return items в different order).
            // cpDesign/paymentsDesign/genBalancesDesign intentionally EXCLUDED from this compare — all three locally owned (see above).
            setUser(prev => {
              if (!prev) return reconciled;
              const prevAccess = JSON.stringify((prev.pageAccess || []).slice().sort());
              const newAccess = JSON.stringify((freshUser.pageAccess || []).slice().sort());
              if (prev.email === freshUser.email && prev.name === freshUser.name && prevAccess === newAccess) {
                return prev;  // unchanged → React skips render
              }
              console.log('🔄 User state refreshed from server probe (pageAccess или name updated)');
              return reconciled;
            });
            saveSession(reconciled);  // обновить localStorage с fresh data (keeps local cpDesign)
          }
          // 'keep' — other status codes (5xx, 429, 403) or ok-without-user → transient, keep local data
        } catch (e) {
          // Network failure / timeout — gracefully degrade. User has local data, может работать.
          // WebSocket reconnect или next user action подтвердит actual session state.
          console.log('⚠️ Session probe failed (network/timeout) — continuing with local-first behavior');
          // Отмену проверяем и здесь: у пробы свой таймаут 5 с, и без этой строки
          // отменённый прогон уходил в Step 2 и выпускал полтора десятка запросов уже
          // после ухода со страницы. Любой 401 среди них поднимает модалку «сессия
          // истекла» и стирает токен — у следующего пользователя вкладки.
          // Выйти отсюда безопасно: localStorage ещё не тронут (его перезаписывает
          // только Step 2 ниже).
          if (cancelled) return;
        }
      }
      // ═══════════════════════════════════════════════════════════════════════

      // Step 2: Fetch server data + MERGE in background (if online + authenticated)
      if (hasToken && serverOnline) {
        const [su, sp, scp, scrg, sdc, sdl, swl, sof, spt, scf, scpart, sftd, sdcalc, ssel, saff] = await Promise.all([
          apiGet('users'), apiGet('payments'), apiGet('customer-payments'),
          apiGet('crg-deals'), apiGet('daily-cap'), apiGet('deals'), apiGet('wallets'), apiGet('offers'), apiGet('partners'), apiGet('conference-folders'), apiGet('conference-partners'), apiGet('ftd-entries'), apiGet('daily-calcs-data'),
          apiGet('selling-offers'),
          apiGet('daily-cap-aff-caps'), // v32.17.x Daily Cap v2 Э3: настройки аффилиат-капов
        ]);
        // ЗДЕСЬ проверки отмены НЕТ намеренно (жюри по данным, 05.09). apiGet уже
        // перезаписал localStorage чистыми серверными данными, а строки, которые есть
        // только локально, возвращает в кэш ТОЛЬКО слияние ниже (mergeByID + lsSave).
        // Выйти между этими двумя точками — значит потерять их безвозвратно, мимо
        // pendingSaves и склада спасённых правок. Этап слияния обязан доработать:
        // он пишет в localStorage, а setState на размонтированном дереве в React 18
        // безвреден.
        if (sessionExpiredFlag) {
          setSessionExpiredFlag(false);
          // 2026-08-18 — тут стоял свой контрольный запрос (v31.66, «защита от ложной
          // тревоги перед массовым логаутом»), и он был самосбывающимся пророчеством:
          // apiGet стирает токен ДО этой строки, запрос уходил через authHeaders() БЕЗ
          // заголовка и получал 401 при любой погоде. То есть «подтверждение смерти»
          // выдавалось всегда, и человека разлогинивало по сетевой икоте — ровно та
          // жалоба, ради которой v31.66 и писался. Ветка FALSE_ALARM была недостижима.
          //
          // Теперь у решения «сессия мертва» ОДИН хозяин — confirmSessionExpired
          // (src/lib/session-expiry.js): он спрашивает сервер ТЕМ ЖЕ токеном, снятым до
          // стирания, хоронит только по двум 401 подряд, сам показывает модалку с
          // предложением перезагрузить, а живую сессию возвращает на место. Дублировать
          // его вердикт сломанной проверкой нечем и незачем.
          console.warn('⚠️ sessionExpiredFlag: вердикт за confirmSessionExpired (session-expiry.js)', {
            fetchResults: { su:!!su, sp:!!sp, scp:!!scp, scrg:!!scrg, sdc:!!sdc, sdl:!!sdl, swl:!!swl, sof:!!sof, spt:!!spt, sftd:!!sftd, sdcalc:!!sdcalc },
            justLoggedIn,
            ts: new Date().toISOString(),
          });
        }
        // v31.66: bug fix — anySuccess массив включал 10 endpoints, но Promise.all
        // возвращал 11 (sdcalc=daily-calcs-data был omitted). В edge case где ТОЛЬКО
        // sdcalc returned non-null, anySuccess=false → если justLoggedIn=false, fall
        // through к остальной логике без data merge → silent stale state.
        const anySuccess = [su, sp, scp, scrg, sdc, sdl, swl, sof, spt, sftd, sdcalc].some(d => d !== null);
        if (!anySuccess && justLoggedIn) {
          console.log("\u26A0\uFE0F All fetches failed during login grace \u2014 using local data");
          // setLoaded already called above — just release save lock
          releaseSaveLockAfter(4000); return;
        }

        if (anySuccess) {
          const pushTasks = [];

          // USERS — v10.16: Server is source of truth for pageAccess
          if (su !== null) {
            const serverMap = new Map((Array.isArray(su) ? su : []).map(u => [u.email, u]));
            // 2026-10-21: досев из INITIAL_USERS тех, кого сервер не вернул, убран — он
            // рисовал уволенных (Zack, Nayan) призраками в User Management. Стартовые
            // записи сервер создаёт сам (seedUsers), его список — единственный источник.
            // v31.42 Fix C1 (post-audit): frontend больше НЕ seed'ит passwordHash.
            // Причина: GET /api/users stripпит password fields → frontend mass save
            // отправлял seed hash как "current" → backend migration думал что это
            // password change → bcrypt'ил seed hash → SEED PASSWORD становился valid
            // login credential (authentication bypass для всей seed roster).
            // Теперь: password fields всегда undefined на клиенте, backend preserve
            // logic восстанавливает из existing users.json при mass save. Admin password
            // changes идут через explicit user input (editUser.password field) — new
            // hash добавляется только для edited user, не для всех одновременно.
            const usersFinal = Array.from(serverMap.values()).map(u => {
              // Strip any password fields that slipped in — defense in depth
              const { passwordHash, passwordBcrypt, ...rest } = u;
              return rest;
            });
            setUsers(usersFinal);
            saveBaselines.current['users'] = JSON.stringify(usersFinal);
            // DON'T push users to server on load — server owns pageAccess
          }

          // DATA TABLES \u2014 merge by ID
          const tables = [
            { key: 'payments', srv: sp, setter: setPayments, ref: 'payments' },
            { key: 'customer-payments', srv: scp, setter: setCpPayments, ref: 'customer-payments' },
            { key: 'crg-deals', srv: scrg, setter: setCrgDeals, ref: 'crg-deals' },
            { key: 'daily-cap', srv: sdc, setter: setDcEntries, ref: 'daily-cap' },
            { key: 'deals', srv: sdl, setter: setDealsData, ref: 'deals' },
            { key: 'wallets', srv: swl, setter: setWalletsData, ref: 'wallets' },
            { key: 'partners', srv: spt, setter: setPartnersData, ref: 'partners' },
            { key: 'conference-folders', srv: scf, setter: setConfFoldersData, ref: 'conference-folders' },
            { key: 'conference-partners', srv: scpart, setter: setConfPartnersData, ref: 'conference-partners' },
            { key: 'selling-offers', srv: ssel, setter: setSellingOffers, ref: 'selling-offers' },
            { key: 'ftd-entries', srv: sftd, setter: setFtdEntries, ref: 'ftd-entries' },
            { key: 'daily-calcs-data', srv: sdcalc, setter: setDailyCalcs, ref: 'daily-calcs-data' },
          ];
          for (const t of tables) {
            if (t.srv !== null) {
              // P4-GUILLOTINE (2026-08-31, тихая потеря правок): local-only records older
              // than mergeByID's unified 7d threshold no longer vanish with just a
              // console.warn — onDropStale rescues them into the same durable
              // blitz_dropped_saves store flushPendingSaves uses after exhausted retries.
              const merged = mergeByID(local[t.key], t.srv, t.key, undefined, (droppedRecord) => {
                persistDroppedSave(t.key, { data: [droppedRecord], reason: 'merge-stale-local' });
              });
              t.setter(merged);
              lsSave(t.key, merged);
              lastKnownCounts[t.key] = merged.length; // Set baseline for data loss protection
              // P3-BOOTMERGE (2026-08-31, тихая потеря правок): was `merged.length >
              // t.srv.length` — an EDIT of an EXISTING row (same count, newer updatedAt)
              // never grew the count, so it was never pushed AND the guard below got
              // seeded with the unconfirmed local snapshot (silently "delivered"). Content
              // comparison catches edits too; on divergence, seed with the SERVER snapshot
              // instead so a later debouncedSave/retry still sees a real diff to send.
              const { diverged, baselineJson } = computeBootMergePush(merged, t.srv);
              if (t.ref) {
                saveBaselines.current[t.ref] = baselineJson;
                if (lastSavedHash.current) lastSavedHash.current[t.ref] = baselineJson; // v10.28: seed server-sent hash
              }
              if (diverged) pushTasks.push(apiSave(t.key, merged, user?.email));
            } else if (local[t.key] && local[t.key].length > 0 && !isEndpointForbidden(t.key)) {
              // 2026-07-28 (аудит прав): `t.srv === null` означает И «сервер не ответил»,
              // И «нет прав на таблицу» — apiGet в обоих случаях отдаёт null. Для второго
              // случая локальная дозаливка неверна: снимок `local` собран из localStorage
              // ДО запросов, поэтому здесь показали бы запрещённые строки из старого кэша.
              // Отправку назад уже блокирует сам apiSave, но рисовать эти данные тоже
              // не нужно — состояние остаётся [], как при пустом кэше.
              t.setter(local[t.key]);
              lastKnownCounts[t.key] = local[t.key].length; // Set baseline for data loss protection
              // v32.16.91 (Sentry NODE-EXPRESS-8, 61 событий с 29.05): t.ref — СТРОКОВЫЙ ключ
              // (см. таблицу выше), а тут был реликт эпохи real-ref'ов: `t.ref.current = ...`
              // на строке → TypeError в strict mode → ВЕСЬ boot-цикл обрывался на середине
              // (остальные таблицы не инициализировались, локальная дозаливка пропадала) —
              // ещё один поставщик stale-вкладок. Зеркалим успешную ветку (1758-1761):
              // baseline по строковому ключу + seed lastSavedHash.
              if (t.ref) {
                const localJson = JSON.stringify(local[t.key]);
                saveBaselines.current[t.ref] = localJson;
                if (lastSavedHash.current) lastSavedHash.current[t.ref] = localJson;
              }
              pushTasks.push(apiSave(t.key, local[t.key], user?.email));
            }
            // NO FALLBACK — if both server and localStorage empty, state stays [] (safe)
          }

          // v32.17.x Daily Cap v2 Э3: aff-caps простой replace (full-array, без ID-merge)
          if (Array.isArray(saff)) { setAffCaps(saff); }

          // ── Merge Telegram offers into deals (startup) ──
          // v31.09: compound key (affiliate, country) instead of id-only — replace older с newer
          // по updatedAt/createdAt/date. Это предотвращает накопление duplicates при каждом
          // bot update (старый баг: 535 deals вместо ~484 unique pairs).
          if (sof !== null && Array.isArray(sof) && sof.length > 0) {
            setOffersRaw(sof); // keep raw offers separately for dashboard count
            lsSave('offers', sof);
            setDealsData(prev => {
              const { merged, added, replaced } = mergeOffersIntoDeals(prev || [], sof);
              if (added > 0 || replaced > 0 || merged.length !== (prev || []).length) {
                console.log(`📋 Merged Telegram offers into deals: +${added} new, ~${replaced} replaced (${(prev || []).length} → ${merged.length})`);
                lsSave('deals', merged);
                saveBaselines.current['deals'] = JSON.stringify(merged);
                lastKnownCounts['deals'] = merged.length;
                return merged;
              }
              return prev;
            });
          }

          if (pushTasks.length > 0) await Promise.all(pushTasks);
        }
      }

      // setLoaded already called above (before server fetch) — app shows immediately from localStorage
      releaseSaveLockAfter(8000, '🔓 Save lock released — auto-save enabled'); // 8s (was 12s) — app is already shown, don't block saves so long
    })().catch((err) => {
      // Ошибка загрузки больше не улетает в никуда — но и не исчезает: раньше её
      // подбирал автоматический обработчик unhandled rejection в Sentry, теперь
      // отправляем явно. Молчаливый catch тут был бы фолбэком, прячущим баг.
      console.error('[boot] загрузка приложения оборвалась:', err);
      try { Sentry.captureException(err, { tags: { area: 'app-boot' }, extra: { cancelled } }); } catch { /* Sentry выключен без DSN */ }
      // Замок сохранения НЕ снимаем намеренно: состояние вкладки после оборванной
      // загрузки неизвестно, и включать автосейв поверх полузагруженных данных —
      // решение владельца, а не побочный эффект отлова ошибки (пункт 60j ревью
      // Tier 1 от 16.05, вынесен в отчёт). Состояние делаем видимым: тост один раз,
      // пока boot-замок ещё держит (если таймер уже снял — молчим).
      if (!cancelled && isBootSkipSaveHeld()) {
        markBootFailed();
        dispatchToast('Page did not finish loading — changes are not being saved. Please reload the page.', 'error');
      }
    });
    return () => {
      cancelled = true;
      lockTimers.forEach(clearTimeout);
    };
    // 2026-05-16 ESLint burndown: setCpPayments/setDcEntries/setPayments are useCallback([])
    // wrappers (App.jsx:4535/4536/4582) — TRULY stable refs, never change identity. ESLint
    // doesn't auto-detect this stability (not direct useState destructure). Safe additions.
  }, [user, setCpPayments, setDcEntries, setPayments]); // Re-run when user logs in

  // \u2500\u2500 WebSocket Real-Time Sync \u2014 MERGE, never replace \u2500\u2500
  useEffect(() => {
    if (!loaded || !getSessionToken()) return;
    connectWebSocket();
    // 2026-09-23: колбэк вынесен в src/lib/app-ws-listener.js — чтобы тестировать его вместе с
    // настоящим onmessage из api-client. Тот же колбэк применяет и снимки опроса ниже (N1).
    const applyServerUpdate = createAppWsListener({
      setters: {
        users: setUsers, payments: setPayments, 'customer-payments': setCpPayments,
        'crg-deals': setCrgDeals, 'daily-cap': setDcEntries, deals: setDealsData, wallets: setWalletsData, 'ftd-entries': setFtdEntries,
        partners: setPartnersData, 'conference-folders': setConfFoldersData, 'conference-partners': setConfPartnersData, 'daily-calcs-data': setDailyCalcs,
        'selling-offers': setSellingOffers,
        // v32.17.x Daily Cap v2 Этап 3: WS live-обновление aff-caps настроек
        'daily-cap-aff-caps': setAffCaps,
      },
      lockSkip: () => lockSkipSave(skipSave),
      rescue: rescueStaleSyncRecord,
      // Объявлены ниже (блок автосейва); к моменту первого сообщения уже инициализированы.
      saveBaselines,
      lastSavedHash,
    });
    const unsub = onWsUpdate(applyServerUpdate);

    // Fallback polling every 15s — MERGE, never replace
    const poll = async () => {
      if (!getSessionToken() || isSessionKnownDead()) return;
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
      if (wsConnection && wsConnection.readyState === WebSocket.OPEN) return;
      if (!serverOnline) { try { const h = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(3000) }); if (h.ok) {
        const hd = await h.json().catch(()=>({}));
        setServerOnline(true); console.log("🔌 Server back online"); connectWebSocket();
        // Phase 1a: use build-hash (hd.bh) to detect deploys, not version label.
        // Catches mobile that never got a WS heartbeat with the new hash.
        if (hd.bh && INITIAL_BUILD_HASH && hd.bh !== INITIAL_BUILD_HASH && !window.__versionReloadScheduled) {
          window.__versionReloadScheduled = true;
          console.warn(`⚠️ Poll: server build ${hd.bh} ≠ client build ${INITIAL_BUILD_HASH} — reloading in 3s`);
          setTimeout(() => { window.location.reload(true); }, 3000);
        }
      } } catch {} }
      // Also check version on each poll even if already online
      if (serverOnline && !window.__versionReloadScheduled) {
        fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(3000) }).then(r=>r.json()).then(hd=>{
          // Phase 1a: build-hash is the reload signal, not version label.
          if (hd.bh && INITIAL_BUILD_HASH && hd.bh !== INITIAL_BUILD_HASH && !window.__versionReloadScheduled) {
            window.__versionReloadScheduled = true;
            console.warn(`⚠️ Poll: server build ${hd.bh} ≠ client build ${INITIAL_BUILD_HASH} — reloading in 3s`);
            setTimeout(() => { window.location.reload(true); }, 3000);
          }
        }).catch(()=>{});
      }
      // v9.09: If server is online but WS isn't connected, keep trying
      if (serverOnline && (!wsConnection || wsConnection.readyState !== WebSocket.OPEN)) { connectWebSocket(); }
      if (!serverOnline) return;
      const [su, sp, scp, scrg, sdc, sdl, swl, sof, spt, scf, scpart, sftd, sdcalc2, saff2] = await Promise.all([
        apiGet('users'), apiGet('payments'), apiGet('customer-payments'), apiGet('crg-deals'), apiGet('daily-cap'), apiGet('deals'), apiGet('wallets'), apiGet('offers'), apiGet('partners'), apiGet('conference-folders'), apiGet('conference-partners'), apiGet('ftd-entries'), apiGet('daily-calcs-data'),
        apiGet('daily-cap-aff-caps'), // v32.17.x Daily Cap v2 Э3
      ]);
      lockSkipSave(skipSave);
      // v12.16: Handle users separately — updatedAt wins (local beats server if admin just made a change)
      if (su !== null && Array.isArray(su)) {
        const currentUsers = lsGet('users', null) || [];
        const currentMap = new Map(currentUsers.map(u => [u.email, u]));
        const serverMap = new Map(su.map(u => [u.email, u]));
        // Build merged list: server is source of truth for membership (deletions)
        // but if local has a newer updatedAt for a user, keep local pageAccess
        const pollMerged = su.map(srv => {
          const local = currentMap.get(srv.email);
          const localNewer = local && (local.updatedAt || 0) > (srv.updatedAt || 0);
          return {
            ...srv,
            // pageAccess: local wins if admin made a change more recently than server version
            pageAccess: localNewer && Array.isArray(local.pageAccess) ? local.pageAccess
              : (Array.isArray(srv.pageAccess) ? srv.pageAccess : (local?.pageAccess || [])),
            // passwordHash: never sent by server, keep local copy
            passwordHash: local?.passwordHash || undefined,
            // updatedAt: keep the newest
            updatedAt: Math.max(local?.updatedAt || 0, srv.updatedAt || 0) || undefined,
          };
        });
        const currentStr = JSON.stringify(currentUsers.map(u => ({email:u.email,pageAccess:u.pageAccess})).sort((a,b)=>a.email.localeCompare(b.email)));
        const pollStr = JSON.stringify(pollMerged.map(u => ({email:u.email,pageAccess:u.pageAccess})).sort((a,b)=>a.email.localeCompare(b.email)));
        if (currentStr !== pollStr) {
          lsSave('users', pollMerged);
          setUsers(pollMerged);
        }
      }

      // 2026-09-23 (N1): снимки опроса идут тем же путём, что сообщения WS. Раньше здесь был
      // свой merge с lsGet(t.k) — но apiGet уже записал в кэш тот же снимок (api-client.js,
      // apiGet → lsSave), так что сравнение давало «без изменений» и экран не обновлялся ровно
      // так же, как по WS. Заодно crg-deals/daily-cap/aff-caps получают здесь те же правила, что
      // по WS (row-merge / replace), а не отдельную ветку, которая с ними разошлась.
      const polled = [
        ['payments', sp], ['customer-payments', scp], ['crg-deals', scrg], ['daily-cap', sdc],
        ['deals', sdl], ['wallets', swl], ['partners', spt], ['conference-folders', scf],
        ['conference-partners', scpart], ['ftd-entries', sftd], ['daily-calcs-data', sdcalc2],
        ['daily-cap-aff-caps', saff2], // v32.17.x Daily Cap v2 Э3
      ];
      for (const [table, data] of polled) {
        if (data !== null) applyServerUpdate({ type: 'update', table, data, source: 'poll' });
      }
      let anyChanged = false;

      // Merge Telegram offers into deals during poll
      // v31.09: compound-key merge (см. mergeOffersIntoDeals)
      if (sof !== null && Array.isArray(sof) && sof.length > 0) {
        setOffersRaw(sof); lsSave('offers', sof); // keep raw offers for dashboard
        const lsDeals = lsGet('deals', null) || [];
        const { merged: mergedRaw, added, replaced } = mergeOffersIntoDeals(lsDeals, sof);
        if (added > 0 || replaced > 0) {
          const merged = filterTombstoned('deals', mergedRaw); // v11.02 C4: tombstone check
          lsSave('deals', merged);
          setDealsData(merged);
          anyChanged = true;
        }
      }
      } finally {
        pollInFlightRef.current = false;
      }
    };
    const interval = setInterval(poll, 15000); // 15s poll for timely sync
    const reconnectFlush = setInterval(() => { if (serverOnline && pendingSaves.size > 0) flushPendingSaves(); }, 10000);
    return () => { clearInterval(interval); clearInterval(reconnectFlush); unsub(); };
    // 2026-05-16 ESLint burndown: useCallback([]) stable setter wrappers (see line ~5048 PR#94 comment).
  }, [loaded, setCpPayments, setDcEntries, setPayments, authRev]);

  // ── Unified debounced auto-save (v10.28) ──
  // Single system: debounce 3s (matches server throttle), skip if unchanged from baseline, block during init
  // v10.28 FIX: lastSavedHash tracks the exact JSON that was last successfully sent to the server.
  // This prevents the autosave loop where the baseline drifted out of sync with the server state,
  // causing zero-change saves to fire repeatedly (287 saves/day observed in diagnostics).
  const saveBaselines = useRef({});
  const saveTimers = useRef({});
  const lastSavedHash = useRef({}); // v10.28: tracks what was ACTUALLY sent to server
  // 2026-05-16 Tier 1 review F2 fix (CRITICAL audit attribution):
  // pendingSaveInitiator preserves the user who triggered the FIRST debouncedSave call
  // в a pending save window. Without this, useEffect refire на canSaveTable change
  // (caused by user logout/login during 3s debounce) would clearTimeout A's pending
  // timer + reschedule new one с B's closure → A's edit attributed к B in audit log.
  // pendingSaveData lets us distinguish "useEffect refire с identical data" (preserve
  // initiator) vs "user made a NEW edit" (replace initiator with current user).
  const pendingSaveInitiator = useRef({}); // {table: userEmail at first-call time}
  const pendingSaveData = useRef({});       // {table: stringified data of pending save}

  // v11.01: Track the last updatedAt seen in users — only push users to server when admin made a real edit
  const lastUsersUpdatedAt = useRef(0);

  const debouncedSave = (table, data, isReschedule = false) => {
    if (!serverFetchDone.current || !loaded) return;
    // 2026-05-15 Aff 140 cascade fix — rescheduled debouncedSave holds closure-captured
    // data from initial call. If WS broadcast updated localStorage between schedule + retry,
    // closure is stale (would overwrite local AND queue stale POST → server cascade fire).
    // localStorage is authoritative source-of-truth: updated by both user actions (line ~5249)
    // AND WS handler (line ~5102). Mismatch с closure → WS arrived → abandon reschedule.
    // Only checked on isReschedule=true — initial user-action calls have new state that
    // hasn't yet been lsSave'd (mismatch is expected, not stale).
    if (isReschedule) {
      const lsCurrent = lsGet(table, null);
      if (lsCurrent && JSON.stringify(lsCurrent) !== JSON.stringify(data)) {
        console.log(`🔕 ${table} debouncedSave reschedule abandoned — stale closure (WS update during skipSave window)`);
        return;
      }
    }
    // v32.03 Bug #59: entry-skipSave retry (same class as #53 inner-timer fix v31.97).
    // Was: silently returned если skipSave true at scheduling time — "final user action
    // before navigating away" was lost. Now: schedule retry past skipSave window.
    if (skipSave.current) {
      if (shouldRemindBootFailed()) {
        dispatchToast('Page did not finish loading — changes are not being saved. Please reload the page.', 'error');
      }
      setTimeout(() => debouncedSave(table, data, true), 2500); // mark retry as reschedule
      return;
    }
    if (!Array.isArray(data)) return;
    // v11.01: For users table, only push if an admin edit happened (updatedAt changed)
    // This prevents the polling merge from triggering a redundant users save that could overwrite pageAccess
    if (table === 'users') {
      const maxUpdatedAt = data.reduce((m, u) => Math.max(m, u.updatedAt || 0), 0); // v11.02 H5: avoid spread stack overflow
      if (maxUpdatedAt <= lastUsersUpdatedAt.current) return; // No admin edit since last save
      lastUsersUpdatedAt.current = maxUpdatedAt;
    }
    const json = JSON.stringify(data);
    // v10.28: Skip if identical to what we LAST SENT to server (not just baseline)
    if (json === (lastSavedHash.current[table] || null)) return;
    // Also skip if identical to baseline (unchanged since last load/merge)
    if (json === (saveBaselines.current[table] || '[]')) return;
    lsSave(table, data);
    // FIX: filter invalid deals rows client-side so server never sees them (was filtering server-side
    // but client kept resending the same corrupt rows on every save, causing repeated log warnings)
    // FIX: filter invalid deals before save AND clean from state so they stop being re-sent every cycle
    const dataToSave = (table === 'deals')
      ? (() => {
          const filtered = data.filter(r => {
            const aff = String(r.affiliate || r.affiliateId || '').trim();
            const country = String(r.country || '').trim();
            const price = String(r.price || r.cpa || '').trim();
            return aff && country && price && country.length <= 15;
          });
          return filtered;
        })()
      // FIX: only filter $0 payments that are old (>30min) — new payments being entered may have amount=0 temporarily
      // 2026-06-04 (refund-family audit, site a): был `if (amt > 0) return true`, который относил
      // ЛЮБОЙ отрицательный amount к ветке "$0" и дропал его, если запись старше 30 мин. Отрицательные
      // суммы — это легитимные refund/adjustment записи (refunds Роуз, напр. #212 −$650). Из-за
      // `amt > 0` они молча выпадали ДО POST, поэтому backend-фикс `amt !== 0` (v32.16.15) их не
      // спасал — они не доезжали до сервера. Фикс: keepPaymentRow (чистый, протестированный helper) —
      // любая ненулевая сумма (включая отрицательную) keep; блокируем ТОЛЬКО $0 И пустой invoice
      // старше 30 мин (свежие $0-строки оставляем — их сейчас редактируют).
      : (table === 'payments' || table === 'customer-payments')
        ? data.filter(r => keepPaymentRow(r))
        : data;
    // 2026-05-16 Tier 1 review F2 fix (CRITICAL audit attribution preservation):
    // If a save is already pending for this table AND incoming data is byte-identical
    // к pending data, this is а useEffect refire (canSaveTable changed but state didn't).
    // Preserve original initiator by NOT replacing timer — let A's original timer fire
    // под A's identity rather than transplanting к the currently-logged-in user.
    if (saveTimers.current[table] && pendingSaveData.current[table] === json) {
      return; // refire с no data change — preserve in-flight initiator
    }
    if (saveTimers.current[table]) clearTimeout(saveTimers.current[table]);
    // F2: snapshot initiator at the moment а real save is queued. Subsequent useEffect
    // refires с identical data hit the early-return above and don't overwrite this.
    // Real edits (data changes) clear the old timer + replace initiator с current user
    // (acceptable since the new edit IS being made by the current user).
    pendingSaveInitiator.current[table] = user?.email;
    pendingSaveData.current[table] = json;
    // v31.97 Bug #53: silent-drop fix — when inner timer fires inside skipSave window
    // (e.g., WS sync arrives mid-debounce), don't silent return. Reschedule recursively
    // until skipSave window expires. Save eventually persists, не teryается.
    const performSave = async () => {
      if (skipSave.current) {
        // Recursive reschedule (500ms tick) — skipSave windows are ~2s, so 3-4 retries max
        saveTimers.current[table] = setTimeout(performSave, 500);
        return;
      }
      // 2026-05-15 Aff 140 cascade fix layer 2 — even non-reschedule path может face WS race:
      // useEffect fires с _payments, queues performSave с closure data, then WS broadcast
      // arrives mid-3s wait + updates localStorage. At fire time, closure data is now stale.
      // Skip POST к prevent server-side phantom notification (Cameron-on-#140 pattern).
      // Layer 1 (entry check above) catches reschedule path; this layer catches normal path.
      const lsCurrentSave = lsGet(table, null);
      if (lsCurrentSave && JSON.stringify(lsCurrentSave) !== JSON.stringify(data)) {
        console.log(`🔕 ${table} performSave skipped — stale closure (WS overwrote local before fire)`);
        return;
      }
      const currentJson = JSON.stringify(data);
      // v10.28: Double-check against both baseline AND last server-sent hash
      if (currentJson === (saveBaselines.current[table] || '[]')) return;
      if (currentJson === (lastSavedHash.current[table] || null)) return;
      // 2026-05-16 Tier 1 review F1 fix (CRITICAL silent data loss):
      // Previously: saveBaselines + lastSavedHash were written PRE-await. If apiSave
      // returned false (401/409/network/throttle) or threw, the refs were left "claiming"
      // we'd persisted `currentJson` server-side when in fact we hadn't. The very next
      // call с identical data would short-circuit at L5285/5333 → silent data loss.
      // Now: refs updated ONLY after confirmed-OK apiSave. Failure keeps refs unchanged,
      // so the next legitimate call proceeds (no false dedupe).
      //
      // 2026-05-16 Tier 1 review F2 fix (CRITICAL audit attribution):
      // Read initiator from ref (not closure variable). Initiator was captured at the
      // moment THIS performSave was scheduled (in debouncedSave). useEffect refires
      // с same data don't overwrite it (skipped at the early-return above). New edits
      // (different data) clearTimeout + replace initiator + reschedule — so a fresh
      // performSave с new initiator runs instead. Either way, attribution corresponds
      // к the user who actually triggered the pending save, not whoever is currently
      // logged in at apiSave fire time.
      const myInitiator = pendingSaveInitiator.current[table];
      const myInitJson = pendingSaveData.current[table];
      const ok = await apiSave(table, dataToSave, myInitiator || user?.email); // FIX: use filtered dataToSave
      if (ok) {
        saveBaselines.current[table] = currentJson;
        lastSavedHash.current[table] = currentJson;
      }
      // F2: race-safe cleanup — only clear if pendingSaveData hasn't been replaced by
      // а concurrent debouncedSave call (which would happen if data changed mid-await).
      if (pendingSaveData.current[table] === myInitJson) {
        pendingSaveInitiator.current[table] = null;
        pendingSaveData.current[table] = null;
      }
    };
    saveTimers.current[table] = setTimeout(performSave, 3000); // v10.05: 3s (was 2s) to match server-side throttle
  };

  // ── H-APP2 (2026-06-04 audit, risk: data-loss) — flush armed debounced saves on tab-close / navigation ──
  //
  // БАГ: debouncedSave ставит performSave на 3-секундный setTimeout (см. saveTimers.current[table]
  //   выше). Единственные дренажи pending-save машинерии — reconnect-interval (10s) + module-init
  //   setInterval(flushPendingSaves, 30000) в api-client.js. НЕ было ни одного beforeunload /
  //   pagehide / visibilitychange-хендлера, который бы сбросил армированный saveTimers.current[table].
  //   Если юзер сделал последнюю правку (status / amount) и закрыл вкладку / ушёл навигацией / ноут
  //   ушёл в сон в пределах 3-секундного окна дебаунса — этот POST НИКОГДА не уходил, правка терялась.
  //   v32.03-фикс (entry-skipSave retry) перешедулил только мимо skipSave-окна — он НЕ покрывает
  //   ситуацию реального unload страницы.
  //
  // ПОЧЕМУ так: debouncedSave намеренно дебаунсит на 3с (= server-side throttle), чтобы не спамить
  //   сервер посимвольными правками. Но окно дебаунса == окно потери данных при unload.
  //
  // ФИКС: на pagehide (предпочтительнее beforeunload — работает на mobile/bfcache) + на
  //   visibilitychange→hidden (bfcache/мобильный компаньон) синхронно дослать каждую таблицу, у
  //   которой ЖИВ армированный saveTimers.current[table], через fetch(keepalive:true). keepalive
  //   позволяет запросу пережить выгрузку страницы; authHeaders() несёт Bearer-токен (sendBeacon
  //   НЕЛЬЗЯ — он не умеет ставить Authorization-заголовок, а save-роут требует auth). Тело —
  //   { data, user, deleted:[] } — ровно то, что ждёт generic POST-роут (см. server.cjs
  //   app.post('/api/payments') :2326 — { data, deleted } из req.body; identity берётся из
  //   session-токена, не из body.user; version на этом generic-пути НЕ проверяется на 409,
  //   так что отсутствие version безопасно). pendingSaveData.current[table] хранит JSON, с которым
  //   таймер был поставлен — это и есть та правка, которую performSave бы отправил. Сервер сам
  //   ре-фильтрует junk ($0+blank) через paymentRowIsJunk, поэтому нефильтрованные-но-валидные
  //   данные слать безопасно.
  //
  // Идемпотентность: после успешной отправки гасим таймер + чистим pending-рефы, чтобы 3-секундный
  //   performSave НЕ выстрелил повторно, если страница лишь ушла в фон и потом восстановилась из
  //   bfcache. lastSavedHash здесь НЕ трогаем намеренно — если bfcache вернёт страницу, обычный
  //   performSave/debouncedSave при следующей правке корректно сравнится с baseline.
  const flushArmedSavesOnUnload = () => {
    try {
      const tables = Object.keys(saveTimers.current || {});
      for (const table of tables) {
        const timer = saveTimers.current[table];
        const pendingJson = pendingSaveData.current[table];
        if (!timer || !pendingJson) continue; // нет армированного таймера / нет данных — пропускаем
        let parsed;
        try { parsed = JSON.parse(pendingJson); } catch { continue; }
        if (!Array.isArray(parsed)) continue; // save-роуты ждут массив; не-массив — пропускаем
        const initiator = pendingSaveInitiator.current[table] || user?.email || 'unknown';
        // P2-UNLOAD (2026-08-31, тихая потеря правок): keepalive fetch ниже — fire-and-forget;
        // если он не долетит (офлайн, ОС убивает вкладку на середине, ноут ушёл в сон раньше
        // ответа), правка пропадала БЕЗ единого механизма доставки. Кладём в ОБА восстановимых
        // места ДО сетевой попытки: lsSave — закрытая вкладка дошлёт через чинёный boot-merge
        // (П3) на следующей загрузке; pendingSaves — скрытая, но живая вкладка дошлёт своим же
        // 30-секундным flushPendingSaves, когда сеть вернётся. deleted:[] — как и тело fetch
        // ниже: у unload-пути никогда не было списка удалений для отправки.
        // FR5 (fix-round 2026-08-31, Cursor п.2 / жюри A п.5): `parsed` — снимок,
        // захваченный в момент постановки debounce-таймера. Если WS-правка из другой
        // вкладки успела записать более свежую строку в localStorage ПОСЛЕ этого момента,
        // слепой lsSave(table, parsed) откатил бы её на клиенте (тело keepalive-фетча ниже
        // это не задевает — сервер сам мёржит по updatedAt согласно контракту). Построчный
        // LWW-мерж вместо слепой записи: parsed побеждает только там, где его updatedAt
        // новее; строки, которых в parsed нет, из текущего LS-снимка не трогаются.
        const mergedForUnload = mergeByID(parsed, lsGet(table, null) || [], table);
        lsSave(table, mergedForUnload);
        const pendingDel = deletedIDs[table] ? Array.from(deletedIDs[table]) : [];
        enqueuePendingSave(table, { data: mergedForUnload, userEmail: initiator, deleted: pendingDel });
        const queuedDeleted = pendingSaves.get(table)?.deleted || pendingDel;
        try {
          const body = JSON.stringify({ data: mergedForUnload, user: initiator, deleted: queuedDeleted });
          const bodyBytes = new TextEncoder().encode(body).length;
          if (bodyBytes > 60000) {
            console.warn(`keepalive skipped for ${table}: body ${bodyBytes} bytes > 60000`);
          } else {
            fetch(`${API_BASE}/${table}`, {
              method: 'POST',
              headers: authHeaders(), // Bearer + Content-Type
              body,
              keepalive: true, // переживает выгрузку страницы (до ~64KB на keepalive-запрос)
            }).catch(() => {}); // fire-and-forget — на unload результат уже не обработать
          }
        } catch { /* fetch недоступен / упал — молча, чтобы не сломать unload */ }
        // гасим таймер + чистим pending, чтобы избежать повторного performSave при bfcache-restore
        clearTimeout(timer);
        saveTimers.current[table] = null;
        pendingSaveData.current[table] = null;
        pendingSaveInitiator.current[table] = null;
      }
    } catch { /* defensive — хендлер unload не должен бросать никогда */ }
  };

  useEffect(() => {
    // pagehide покрывает закрытие вкладки + навигацию + (на мобильных) сворачивание в фон.
    // visibilitychange→hidden — дополнительный bfcache/мобильный триггер (iOS Safari часто НЕ
    // шлёт pagehide при свайпе в фон, но visibilityState становится 'hidden').
    const onPageHide = () => flushArmedSavesOnUnload();
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushArmedSavesOnUnload(); };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // users are saved DIRECTLY via saveUsersDirectly() — not via debounced auto-save
  // Debounced auto-save for users was removed: the maxUpdatedAt guard caused saves to be silently skipped
  //
  // 2026-05-15 URGENT SECURITY FIX (auto-save cascade bug — Aff 246 Telegram incident):
  // Pre-fix: ALL useEffects fired debouncedSave для ALL clients unconditionally. When user A
  // changed payment status, WS broadcast updated `_payments` state on EVERY connected client.
  // Each client's useEffect fired → debouncedSave queued → eventually POSTs `/api/payments`
  // с his own session token. Server compared op vs p, saw status difference (stale local
  // или intermediate state), fired notification attributing к WHOEVER's session POSTed.
  // Result: chain reaction. Y Admin clicked Approve → fires "by Y Admin". Then Zack's
  // client auto-saved → fires "by Zack" → broadcast → Alehandro's client auto-saved →
  // fires "by Alehandro". Notification attribution was correct (req.userSession.email),
  // но user intent was wrong. Plus security gap: any authenticated user could mutate ANY
  // table regardless of pageAccess (frontend gates were cosmetic only).
  //
  // Fix Layer 2 (frontend): gate useEffects by table→page mapping. Only fire debouncedSave
  // if user has pageAccess к а page that LEGITIMATELY writes к that table.
  // (Layer 1 backend requireTableAccess middleware is separate gate в server.cjs.)
  // Admin bypass: isAdmin always allowed. userAccess from getPageAccess(user) — derived
  // synchronously via `userAccess` variable above (line ~5429).
  //
  // Table → allowed-page mapping (verified via grep apiSave('TABLE', ...) callsites):
  //   payments         → 'payments' (Dashboard) + 'dailycalcs' (DailyCalcs handlePayField/addAffPay)
  //   customer-payments→ 'customers' (CustomerPayments) + 'dailycalcs' (addCpPay)
  //   crg-deals        → 'crg' (CRGDeals) + 'ftdsinfo' (syncFtdToCrg from FtdsInfo)
  //   deals            → 'deals' (DealsPage / Offers)
  //   partners         → 'partners' (PartnersPage)
  //   ftd-entries      → 'ftdsinfo' (FtdsInfoPage)
  //   daily-calcs-data → 'dailycalcs' (DailyCalcsPage)
  //   wallets          → 'settings' (SettingsPage) — admin-leaning
  // 2026-05-15: extracted к src/utils/permission-gate.js as single source of truth +
  // testable in isolation. Map mirrors backend/services/permission-gate.cjs (drift
  // detected by src/utils/permission-gate.test.js).
  const canSaveTable = React.useCallback((table) => canSaveTableHelper(table, user), [user]);
  // 2026-05-16 ESLint burndown — 8x eslint-disable on debouncedSave deps.
  //
  // 🛡 CASCADE DEFENSE PRESERVATION:
  // debouncedSave is inline-recreated each render BY DESIGN (App.jsx:5252-5360). Each
  // useEffect captures the LATEST closure each render — this freshness is the safety
  // property that 4-layer cascade defense (PR#39/#40/#41/#42 — Aff 246 / Payment #140
  // incident) relies on.
  //
  // Wrapping debouncedSave в useCallback was investigated and DEFERRED:
  // - Would introduce stale-`user` vector для audit attribution (mid-debounce-after-logout
  //   edge case NOT protected by L1-L4 staleness checks — which only verify `data` vs
  //   localStorage, не `user?.email` passed к apiSave)
  // - Would change refire semantics of 8 useEffects (extra fires on user/loaded changes)
  // - Would require dedicated PR с sub-agent adversarial review per cascade-fix precedent
  //
  // Suppression preserves verified behavior. See: project_cascade_root_fix.md.
  const runFinanceAutosave = (table, rows) => {
    runAtomicAutosaveBaselineGate({
      table,
      rows,
      savedBaseline: saveBaselines.current[table],
      lastSavedHash: lastSavedHash.current[table],
      onAtomicBaseline: (serialized) => {
        saveBaselines.current[table] = serialized;
        lastSavedHash.current[table] = serialized;
      },
      onSave: () => debouncedSave(table, rows),
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('payments') && !hasPendingAtomicSnapshots(_payments)) runFinanceAutosave('payments', _payments); }, [_payments, canSaveTable]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('customer-payments') && !hasPendingAtomicSnapshots(_cpPayments)) runFinanceAutosave('customer-payments', _cpPayments); }, [_cpPayments, canSaveTable]);
  // ── 2026-06-12: auto-save effect для crg-deals УДАЛЁН (echo-cascade root cause) ──
  // ЧТО БЫЛО: `useEffect(() => debouncedSave('crg-deals', crgDeals), [crgDeals])` —
  // ЛЮБОЕ изменение state (включая применение ЧУЖОГО WS-броадкаста после истечения
  // skipSave-окна: skipSave 2000ms vs debounce-retry 2500ms — окна не совпадали)
  // отстреливало ВЕСЬ массив из state ЭТОЙ вкладки на сервер. Инцидент 2026-06-12:
  // owner правит CAP Rec в 3 строках → его stale-вкладка шлёт 9-часовой снимок
  // (единственная запись на диск = ОТКАТ .123 TR.capReceived 2→1), а утром вкладки
  // cameron/zack/office эхом «сохранились» за 7 секунд после его сейва.
  // ЧТО ТЕПЕРЬ: каждый мутирующий хендлер CRGDeals шлёт ТОЛЬКО изменённые строки
  // через saveCrgRows → apiSaveRows (см. ниже + src/lib/api-client.js). Сервер
  // мержит по id (row-level LWW, crud-post.cjs:386-450) — частичный массив безопасен.
  // crgDeals state остаётся (рендер + WS row-merge), lsSave работает как кэш.
  // НЕ удалять соседние эффекты других таблиц — они вне scope этого фикса.
  // v31.18: debouncedSave для daily-cap удалён — dcEntries computed from crgDeals,
  // не должен писаться обратно в server (это перезапишет legacy daily-cap.json как
  // computed-derived данные, теряя orphan entries вроде 31/03 Katie). daily-cap.json
  // остаётся read-only backup на сервере (для git history / rollback если что).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('deals')) debouncedSave('deals', dealsData); }, [dealsData, canSaveTable]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('wallets')) debouncedSave('wallets', walletsData); }, [walletsData, canSaveTable]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('partners')) debouncedSave('partners', partnersData); }, [partnersData, canSaveTable]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('conference-folders')) debouncedSave('conference-folders', confFoldersData); }, [confFoldersData, canSaveTable]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('conference-partners')) debouncedSave('conference-partners', confPartnersData); }, [confPartnersData, canSaveTable]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('ftd-entries')) debouncedSave('ftd-entries', ftdEntries); }, [ftdEntries, canSaveTable]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canSaveTable('daily-calcs-data')) debouncedSave('daily-calcs-data', dailyCalcs); }, [dailyCalcs, canSaveTable]);

  // ── 2026-06-12 per-row saves для CRG (замена удалённого auto-save effect'а выше) ──
  // Прокидывается в <CRGDeals saveRows={...}>: каждый мутирующий хендлер страницы шлёт
  // ТОЛЬКО изменённые строки (+ deletedIds → tombstones). apiSaveRows коалесцирует,
  // держит LS-кэш когерентным и ретраит с durable rescue. user?.email — для audit trail.
  const saveCrgRows = React.useCallback(
    (rows, deletedIds) => apiSaveRows('crg-deals', rows, user?.email, deletedIds),
    [user?.email]
  );

  // Global refresh: re-fetch all data from server
  const handleRefresh = async () => {
    addToast('🔄 Refreshing data...', 'info');
    // v31.78 fix: lsKey передавался как storage-key ('blitz_*') но lsSave ожидает
    // logical key ('crg-deals' и т.п.) и сам resolves через LS_KEYS map. Старый
    // вызов lsSave('blitz_crg', ...) делал localStorage.setItem(undefined, ...)
    // → localStorage НЕ обновлялся → следующий WS update читал stale lsData.
    // Now mergeInto принимает logical key.
    const mergeInto = (setter, fresh, logicalKey) => {
      if (!Array.isArray(fresh) || fresh.length === 0) return;
      setter(prev => {
        if (logicalKey === 'daily-calcs-data') {
          // 2026-09-24: Refresh этой таблицы — mergeByID sync (LWW + смена дня).
          // onDropStale тот же, что у начальной загрузки: persistDroppedSave merge-stale-local.
          const mergedDc = mergeByID(prev, fresh, logicalKey, 'sync', (droppedRecord) => {
            persistDroppedSave(logicalKey, { data: [droppedRecord], reason: 'merge-stale-local' });
          });
          lsSave(logicalKey, mergedDc);
          return mergedDc;
        }
        const merged = new Map((prev||[]).map(r=>[r.id,r]));
        fresh.forEach(r=>{ if(r?.id) merged.set(r.id,r); });
        const result = Array.from(merged.values());
        if (logicalKey) lsSave(logicalKey, result);
        return result;
      });
    };
    // v31.78 fix: для crg-deals и daily-cap server-authoritative replace вместо
    // merge. Reason: merge keeps locally-deleted entries (server has 6, prev has 7
    // including stale → result has 7). Replace = пользователь явно хочет server's
    // current state когда жмёт Refresh. Bug C (deletes don't propagate via Refresh)
    // был именно из-за этого.
    const replaceWith = (setter, fresh, logicalKey) => {
      if (!Array.isArray(fresh)) return;
      // SAFETY: don't wipe if server returned empty (could be 401/network glitch)
      // mirrors mergeByID protection (line 11866).
      if (fresh.length === 0) {
        console.warn(`🛑 Refresh ${logicalKey}: server returned empty — skipping (safety)`);
        return;
      }
      setter(fresh);
      lsSave(logicalKey, fresh);
    };
    try {
      const [su, sp, scp, scrg, sdc, sdl, swl, sof, spt, scf, scpart, sftd, sdcalc, saff] = await Promise.all([
        apiGet('users'), apiGet('payments'), apiGet('customer-payments'),
        apiGet('crg-deals'), apiGet('daily-cap'), apiGet('deals'), apiGet('wallets'),
        apiGet('offers'), apiGet('partners'), apiGet('conference-folders'), apiGet('conference-partners'), apiGet('ftd-entries'), apiGet('daily-calcs-data'),
        apiGet('daily-cap-aff-caps'), // v32.17.x Daily Cap v2 Э3
      ]);
      mergeInto(setPayments, sp, 'payments');
      mergeInto(setCpPayments, scp, 'customer-payments');
      // v31.78: crg-deals + daily-cap → server-authoritative replace.
      replaceWith(setCrgDeals, scrg, 'crg-deals');
      replaceWith(setDcEntries, sdc, 'daily-cap');
      mergeInto(setDealsData, sdl, 'deals');
      mergeInto(setWalletsData, swl, 'wallets');
      mergeInto(setPartnersData, spt, 'partners');
      mergeInto(setConfFoldersData, scf, 'conference-folders');
      mergeInto(setConfPartnersData, scpart, 'conference-partners');
      mergeInto(setFtdEntries, sftd, 'ftd-entries');
      mergeInto(setDailyCalcs, sdcalc, 'daily-calcs-data');
      // v32.17.x Daily Cap v2 Э3: simple replace (full-array, не merge-by-id)
      if (Array.isArray(saff)) { setAffCaps(saff); }
      if (Array.isArray(sof)) { setOffersRaw(sof); lsSave('offers', sof); }
      addToast('✅ All data synced from server', 'success');
    } catch(e) {
      addToast('❌ Refresh failed: ' + e.message, 'error');
    }
  };

  // Global refresh/revert via window events — triggered by BlitzHeader buttons
  useEffect(() => {
    const doRefresh = () => handleRefresh();
    const doRevert = () => {
      // 2026-09-23: payments and customer-payments no longer re-send the old snapshot — its rows
      // carry their old updatedAt, so the server's newer-wins merge dropped them (and refused a
      // lowered status) while this said "Reverted" and swapped the old table in on screen.
      // Payments undoes through its page's own revert (registerRevert); Daily Calcs records no
      // undo of its own (the entry it used to pop was the Payments page's); Customer Payments'
      // only entry is a delete, which the server keeps tombstoned — nothing to offer (22.09).
      const PAGE_TABLE = { payments:'payments', crg:'crg-deals',
        deals:'deals', dailycap:'daily-cap', partners:'partners', ftdsinfo:'ftd-entries' };
      const tbl = PAGE_TABLE[page];
      if (!tbl) { addToast('↩ Nothing to revert on this page', 'info'); return; }
      const entry = popUndo(tbl);
      if (!entry) { addToast('↩ No recent changes to revert', 'info'); return; }
      const pageRevert = pageReverts.current[tbl];
      if (pageRevert) { pageRevert(entry.data, entry.label, entry.meta); return; }
      // Apply data back to the correct state setter
      const SETTERS = {
        // 2026-10-07: 'crg-deals' здесь больше нет — CRG откатывает построчно своим обработчиком
        // (registerRevert → pageRevert выше, src/lib/crg-revert.js); снимок всей таблицы
        // откатывал чужие правки.
        'deals': (d) => { setDealsData(d); apiSave('deals', d, user?.email); },
        'daily-cap': (d) => { setDcEntries(d); apiSave('daily-cap', d, user?.email); },
        'partners': (d) => { setPartnersData(d); apiSave('partners', d, user?.email); },
        'conference-folders': (d) => { setConfFoldersData(d); apiSave('conference-folders', d, user?.email); },
        'conference-partners': (d) => { setConfPartnersData(d); apiSave('conference-partners', d, user?.email); },
      };
      const setter = SETTERS[tbl];
      if (setter && entry.data) {
        setter(entry.data);
        addToast('↩ Reverted: ' + (entry.label || 'last change'), 'success');
      } else {
        addToast('↩ Could not apply revert', 'error');
      }
    };
    window.addEventListener('blitz-refresh', doRefresh);
    window.addEventListener('blitz-revert', doRevert);
    return () => { window.removeEventListener('blitz-refresh', doRefresh); window.removeEventListener('blitz-revert', doRevert); };
    // 2026-05-16 ESLint burndown: intentional event-listener binding. Missing deps flagged:
    // - `handleRefresh`: inline async fn (line 5392) recreated each render — adding к deps would
    //   re-bind listeners every render = wasteful. The closure inside doRefresh always calls
    //   FRESH handleRefresh at invocation time (delegate pattern).
    // - `popUndo`: prop from parent, stable (useCallback([], ...) at App.jsx:4604). Could add
    //   safely, но irrelevant given handleRefresh forces suppression anyway.
    // - setDcEntries: useCallback([]) stable wrapper (App.jsx:4536). Could add safely, но не
    //   нужно since other 2 force suppression anyway. (setPayments/setCpPayments left 2026-09-23.)
    // Effect intent: bind global window listeners ONCE per page+user change. Listeners
    // call functions through closure при event-fire (not effect-fire). Delegate pattern OK.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, user]);

  // v31.01 perf: stable callbacks — page components can treat as referentially stable
  const handleLogout = useCallback(() => { clearSession(); setUser(null); setPage("overview"); }, []);

  // CP layout preference (per-account UI pref, like backgroundFilename) — CP redesign 2026-06-21.
  // Optimistic: update local user + localStorage instantly, then persist to server best-effort.
  // NOT finance data; server endpoint validates against the whitelist + atomic users.json write.
  const handleCpDesignChange = useCallback((design) => {
    if (!['classic', 'vivid', 'premium'].includes(design)) return;
    if (!user || user.cpDesign === design) return;
    const next = { ...user, cpDesign: design };
    setUser(next);
    saveSession(next);  // sync localStorage so the choice survives a page reload
    fetch(`${API_BASE}/user-cp-design`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ design }),
    }).catch(() => {});  // best-effort — local state already reflects the choice
  }, [user]);

  // Payments tab layout preference — mirrors handleCpDesignChange 1:1 (Payments premium redesign).
  // Optimistic: update local user + localStorage instantly, then persist to server best-effort.
  // NOT finance data; server endpoint validates against the whitelist + atomic users.json write.
  const handlePaymentsDesignChange = useCallback((design) => {
    if (!['classic', 'premium'].includes(design)) return;
    if (!user || user.paymentsDesign === design) return;
    const next = { ...user, paymentsDesign: design };
    setUser(next);
    saveSession(next);  // sync localStorage so the choice survives a page reload
    fetch(`${API_BASE}/user-payments-design`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ design }),
    }).catch(() => {});  // best-effort — local state already reflects the choice
  }, [user]);

  // Gen Balances tab layout preference — mirrors handlePaymentsDesignChange 1:1 (Gen Balances premium redesign).
  // Optimistic: update local user + localStorage instantly, then persist to server best-effort.
  // NOT finance data; server endpoint validates against the whitelist + atomic users.json write.
  const handleGenBalancesDesignChange = useCallback((design) => {
    if (!['classic', 'premium'].includes(design)) return;
    if (!user || user.genBalancesDesign === design) return;
    const next = { ...user, genBalancesDesign: design };
    setUser(next);
    saveSession(next);  // sync localStorage so the choice survives a page reload
    fetch(`${API_BASE}/user-genbalances-design`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ design }),
    }).catch(() => {});  // best-effort — local state already reflects the choice
  }, [user]);
  const goAdmin = useCallback(() => setPage("admin"), []);

  // 2026-09-01 (фаза3 П2, свод P2/P5/P10) — тело onLogin вынесено сюда из LoginScreen's
  // JSX prop (было инлайн-стрелкой на месте рендера) с НОВЫМ порядком вызовов: токен и
  // сессия СТРОГО ПЕРЕД resetSessionExpiredNotice/resumeRowQueues/flushPendingSaves.
  // Раньше (flush->reset->resumeRowQueues шли ПЕРЕД saveSession/setUser) вход-в-модалке
  // (следующий пункт) поймал бы двойную ловушку:
  //   1. flushPendingSaves' probe-first gate (фаза 2, api-client.js) синхронно читает
  //      localSessionExpired(getRawSession()) ДО первого await — если сессия умерла, пока
  //      вкладка стояла открытой (loginTime старше 23h), стартовавший flush-цикл сразу же
  //      return'ит, ничего не отправив, до тех пор пока freshLogin не проштампует свежий
  //      loginTime.
  //   2. App.jsx saveSession читает токен как
  //      `localStorage.getItem('blitz_token') || getSessionToken() || generateSessionToken()`
  //      — случись это ДО того, как вызывающий (LoginScreen/модалка) записал новый токен,
  //      она бы сгенерила случайный клиентский токен вместо настоящего серверного.
  // Оба реальных вызывающих (LoginScreen, форма в модалке) сами зовут setSessionToken ДО
  // completeLogin — двухаргументная ветка ниже остаётся legacy (см. v28.13) без живых
  // вызывающих (все 3 call site — однoаргументные), намеренно не переписана под что-то
  // новое (свод P10).
  function completeLogin(u, tok) {
    setJustLoggedIn(true);
    // v28.13: handle both onLogin(user) and onLogin(token, userObj) from 2FA
    // 2026-08-31 (П1): both branches are a REAL login (2FA or plain) — freshLogin:true
    // stamps loginTime = now, the one place that's allowed to.
    if (typeof u === 'string' && tok && typeof tok === 'object') {
      setSessionToken(u); saveSession(tok, { freshLogin: true }); setUser(tok);
    } else {
      saveSession(u, { freshLogin: true }); setUser(u);
    }
    // 2026-08-18: вход состоялся — снимаем замок «уже сообщали о протухшей сессии»,
    // иначе о смерти СЛЕДУЮЩЕЙ сессии в этой же вкладке никто не скажет. (Фаза3 П1: эта
    // же функция теперь шлёт SESSION_EXPIRED_CLEARED_EVENT — модалка реально закрывается.)
    resetSessionExpiredNotice();
    // 2026-08-27 (жюри): вход происходит БЕЗ перезагрузки, очередь строк живёт в памяти
    // этой же вкладки. Без снятия паузы каждая следующая правка CRG тихо оседала бы в
    // памяти и не уходила на сервер — та же потеря данных, только с другого конца.
    resumeRowQueues();
    // 2026-08-31 (P5-QUEUE): зеркало предыдущей строки для очереди простых saves —
    // flushPendingSaves сама теперь ждёт вход (isSessionKnownDead() gate), но снятие
    // вердикта на строке выше её не будит: без явного вызова она бы дождалась только
    // следующего 30-секундного тика. Fire-and-forget, как и resumeRowQueues.
    flushPendingSaves();
    // 2026-09-27 (design v2, P3): вход — одна из точек прогона репортёра склада
    // (наряду со стартом вкладки и 60с тиком) — записи, ждавшие сессию, уйдут сразу.
    reportFreshDroppedSaves();
    setAuthRev((n) => n + 1);
    setTimeout(() => { setJustLoggedIn(false); }, 10000);
  }

  // 2026-09-01 (фаза3 П2/П3, свод P5) — регистр для входа-в-модалке: SessionExpiredDialog
  // рендерится в App() (родителе AppInner) и читает getReloginContext() напрямую, без
  // проп-прокидывания. Живёт, пока есть залогиненный пользователь; при логауте/
  // размонтировании — снимается (иначе умершая вкладка предложила бы форму входа для
  // пользователя, которого тут уже нет). completeLogin намеренно НЕ в deps: она
  // пересоздаётся на каждый рендер, но замыкает только стабильные вещи (setState-сеттеры +
  // модульные импорты) — перерегистрация на email не нужна.
  useEffect(() => {
    if (!user?.email) return;
    setReloginContext({ email: user.email, onRelogin: completeLogin });
    return () => clearReloginContext();
  }, [user?.email]);

  if (!loaded) return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #080B14 0%, #0C1021 40%, #111729 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <style>{designTokens}{globalTableCSS}{mobileCSS}{darkModeCSS}{injectionsThemeCSS}{userBackgroundCSS}</style>
      {/* Geist webfont moved к index.html <head> (persistent across SPA mount/unmount).
          Previously here — but splash unmounts when session restores fast → <link>
          removed before Injections renders → fallback к Inter. Now persistent. */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
      <div style={{ textAlign: "center", animation: "fadeUp 0.5s ease" }}>
        {I.logo}
        <div style={{ fontSize: 24, fontWeight: 800, color: "#F1F5F9", marginBottom: 8, marginTop: 16, fontFamily: "'JetBrains Mono',monospace" }}>Blitz CRM</div>
        <div style={{ color: "#64748B", fontSize: 14 }}>Loading v{VERSION}...</div>
        <div style={{ color: "#334155", fontSize: 11, marginTop: 6 }}>Restoring from cache</div>
        <div style={{ width: 120, height: 2, background: "rgba(56,189,248,0.15)", borderRadius: 2, margin: "14px auto 0", overflow: "hidden", position: "relative" }}>
          <div style={{ width: "40%", height: "100%", background: "linear-gradient(90deg, transparent, #38BDF8, transparent)", borderRadius: 2, animation: "shimmer 1.5s ease infinite", backgroundSize: "200% 100%" }} />
        </div>
      </div>
    </div>
  );

  if (!user) {
    return (<><LoginScreen onLogin={completeLogin} users={users} /></>);
  }

  // NOTE: cannot use React.useMemo here — this runs AFTER `if (!loaded)` / `if (!user)` early returns,
  // so calling a hook would violate Rules of Hooks (hook count must be stable across renders → error #310).
  const userAccess = getPageAccess(user);
  const canAccess = pg => userAccess.includes(pg);
  const firstPage = userAccess[0] || "overview";

  // Redirect to first allowed page if current page is blocked
  // Admin-only pages: "admin", "security" — checked via isAdmin(email), не pageAccess
  // Other pages — checked via canAccess() → pageAccess.includes()
  const ADMIN_ONLY_PAGES = ["admin", "security", "botmessages"];
  if (ADMIN_ONLY_PAGES.includes(page)) {
    if (!isAdmin(user.email)) { setPage(firstPage); return null; }
  } else if (!canAccess(page)) {
    setPage(firstPage);
    return null;
  }

  if (page === "admin" && isAdmin(user.email)) return (<><AdminPanel users={users} setUsers={setUsers} wallets={walletsData} setWallets={setWalletsData} onBack={() => setPage(firstPage)} user={user} onNavSecurity={() => setPage("security")} onNavBotMessages={() => setPage("botmessages")} /></>);
  if (page === "security" && isAdmin(user.email)) return (<><SecurityPanel onBack={() => setPage("admin")} user={user} /></>);
  if (page === "botmessages" && isAdmin(user.email)) return (<><BotMessagesPanel onBack={() => setPage("admin")} user={user} /></>);
  // Постоянная шапка: один инстанс на позиции 0 фрагмента — React reuse при навигации
  // (без ремаунта/мерцания и без пересоздания OnlineCounter в шапке).
  const PAGE_ACCENT = { overview:"#6366F1", payments:"#0EA5E9", customers:"#0EA5E9", crg:"#F59E0B", dailycap:"#8B5CF6", deals:"#10B981", partners:"#0071E3", roster:"#635BFF", brokers:"#52606d", phonecheck:"#2563EB", monthlystats:"#6366F1", ftdsinfo:"#10B981", dailycalcs:"#EF4444", genbalances:"#10B981", invoicegen:"#F97316", injections:"#10b981", settings:"#64748B", autob2026:"#A855F7", ai:"#A855F7", radar:"#06B6D4" };
  const header = (<BlitzHeader user={user} activePage={page} userAccess={userAccess} onNav={setPage} onAdmin={goAdmin} onLogout={handleLogout} accentColor={PAGE_ACCENT[page] || "#6366F1"} />);
  let pageEl;
  if (page === "payments" && canAccess("payments")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><Dashboard user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} payments={payments} setPayments={setPayments} userAccess={userAccess} pushUndo={pushUndo} popUndo={popUndo} hasUndo={hasUndo} registerRevert={registerRevert} onPaymentsDesignChange={handlePaymentsDesignChange} /></Suspense>);
  else if (page === "customers" && canAccess("customers")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><CustomerPayments user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} payments={cpPayments} setPayments={setCpPayments} userAccess={userAccess} onCpDesignChange={handleCpDesignChange} /></Suspense>);
  else if (page === "crg" && canAccess("crg")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><CRGDeals user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} deals={crgDeals} setDeals={setCrgDeals} saveRows={saveCrgRows} userAccess={userAccess} ftdEntries={ftdEntries} pushUndo={pushUndo} popUndo={popUndo} hasUndo={hasUndo} registerRevert={registerRevert} /></Suspense>);
  else if (page === "dailycap" && canAccess("dailycap")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><DailyCap user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} entries={dcEntries} setEntries={setDcEntries} crgDeals={crgDealsLive} userAccess={userAccess} pushUndo={pushUndo} popUndo={popUndo} hasUndo={hasUndo} /></Suspense>);
  else if (page === "deals" && canAccess("deals")) pageEl = (<ThemeContext.Provider value={forceLightCtx}><Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><DealsPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} deals={dealsData} setDeals={setDealsData} sellingOffers={sellingOffers} setSellingOffers={setSellingOffers} userAccess={userAccess} pushUndo={pushUndo} popUndo={popUndo} hasUndo={hasUndo} defaultView="selling" /></Suspense></ThemeContext.Provider>);
  else if (page === "partners" && canAccess("partners")) pageEl = (<ThemeContext.Provider value={forceLightCtx}><Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><PartnersHub user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} partners={partnersData} setPartners={setPartnersData} confFolders={confFoldersData} setConfFolders={setConfFoldersData} confPartners={confPartnersData} setConfPartners={setConfPartnersData} userAccess={userAccess} pushUndo={pushUndo} popUndo={popUndo} hasUndo={hasUndo} /></Suspense></ThemeContext.Provider>);
  else if (page === "roster" && canAccess("roster")) pageEl = (<ThemeContext.Provider value={forceLightCtx}><Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><RosterPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} /></Suspense></ThemeContext.Provider>);
  else if (page === "brokers" && canAccess("brokers")) pageEl = (<ThemeContext.Provider value={forceLightCtx}><Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><BrokersPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} /></Suspense></ThemeContext.Provider>);
  else if (page === "phonecheck" && canAccess("phonecheck")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><PhoneCheckPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} /></Suspense>);
  else if (page === "radar" && canAccess("radar")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><RadarPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} /></Suspense>);
  else if (page === "monthlystats" && canAccess("monthlystats")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><MonthlyStatsPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} crgDeals={crgDealsLive} dcEntries={dcEntries} cpPayments={cpPayments} payments={payments} dealsData={dealsData} partnersData={partnersData} ftdEntries={ftdEntries} userAccess={userAccess} /></Suspense>);
  else if (page === "ftdsinfo" && canAccess("ftdsinfo")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><FtdsInfoPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} ftdEntries={ftdEntries} setFtdEntries={setFtdEntries} crgDeals={crgDealsLive} setCrgDeals={setCrgDeals} userAccess={userAccess} /></Suspense>);
  else if (page === "dailycalcs" && canAccess("dailycalcs")) pageEl = (<ThemeContext.Provider value={forceLightCtx}><Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><DailyCalcsPage user={user} onLogout={handleLogout} onNav={setPage} calcs={dailyCalcs} setCalcs={setDailyCalcs} payments={payments} setPayments={setPayments} cpPayments={cpPayments} setCpPayments={setCpPayments} userAccess={userAccess} pushUndo={pushUndo} popUndo={popUndo} hasUndo={hasUndo} /></Suspense></ThemeContext.Provider>);
  else if (page === "genbalances" && canAccess("genbalances")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><GenBalancesPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} onGenBalancesDesignChange={handleGenBalancesDesignChange} /></Suspense>);
  else if (page === "invoicegen" && canAccess("invoicegen")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><InvoiceGeneratorPage user={user} users={users} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} /></Suspense>);
  // v32.16.65: analytics route removed (вкладка убрана; stale page==='analytics' безопасно падает в default OverviewDashboard внизу chain)
  else if (page === "injections" && canAccess("injections")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><InjectionsPage user={user} users={users} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} /></Suspense>);
  // Phase 2: SettingsPage is React.lazy → wrap with Suspense fallback. Per-callsite wrap chosen
  // over chain-level wrap because other routes are still eager; future lazy pages get same shape.
  else if (page === "settings" && canAccess("settings")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><SettingsPage user={user} onLogout={handleLogout} onNav={setPage} userAccess={userAccess} dcEntries={dcEntries} /></Suspense>);
  else if (page === "autob2026" && canAccess("autob2026")) pageEl = (<Suspense fallback={<div style={{padding:40, color:'#888', textAlign:'center', fontSize:14}}>Loading…</div>}><AutoB2026Page user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} /></Suspense>);
  else if (page === "ai" && canAccess("ai")) pageEl = (<AiAdvisorPage user={user} onLogout={handleLogout} onNav={setPage} onAdmin={goAdmin} userAccess={userAccess} />);
  else pageEl = (<OverviewDashboard user={user} onLogout={handleLogout} onNav={setPage} crgDeals={crgDealsLive} dcEntries={dcEntries} dealsData={dealsData} offersRaw={offersRaw} partnersData={partnersData} ftdEntries={ftdEntries} userAccess={userAccess} />);
  return (<>{header}{pageEl}</>);
}






export default function App() {
  const [dark, setDark] = useState(() => {
    // M4 Rev 2: defensive read — Safari private mode / blocked storage throws.
    try {
      const stored = localStorage.getItem('blitz_dark');
      return stored === null ? true : stored === 'true';
    } catch (err) {
      console.warn('[Theme] localStorage read failed (private mode?):', err.message);
      return true; // default dark
    }
  });
  // M4 Rev 2: defensive write — localStorage.setItem can throw in Safari private
  // mode / quota-exceeded / blocked-storage. Now that toggle is user-clickable
  // button (was dead code before), this code path runs on every click — risk
  // multiplied. Theme still switches for current session (state update succeeds),
  // only persistence-across-reload is lost. Silent degradation acceptable here.
  const toggle = () => setDark(prev => {
    const n = !prev;
    try {
      localStorage.setItem('blitz_dark', n);
    } catch (err) {
      console.warn('[Theme] localStorage write failed (private mode?):', err.message);
    }
    return n;
  });

  // 2026-09-27 (design v2, P3) — репортёр склада несохранённых правок: один раз за
  // жизнь вкладки, рядом с остальными корневыми синглтонами (SessionExpiredGate ниже).
  useEffect(() => { installDroppedSaveReporter(); }, []);

  return (
    React.createElement(CrashGuard, null,
      React.createElement(ThemeContext.Provider, { value: { dark, toggle } },
        React.createElement(ToastProvider, null,
          React.createElement("div", { id: "blitz-theme-root", className: dark ? "dark-mode" : "" },
            React.createElement(OfflineBanner, null),
            React.createElement(AppInner, null),
            React.createElement(SessionExpiredGate, null),
            React.createElement(DroppedSavesNotice, null)
          )
        )
      )
    )
  );
}
